import React, { useState, useEffect, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { useQuery } from '@tanstack/react-query';
import { HexColorPicker } from 'react-colorful';
import { Lock } from 'lucide-react';
import type { PrintableColor, PrintColorOption } from '@customedge/shared';
import { FULL_COLOR, DEFAULT_PRINT_COLOR_OPTIONS, printColorOptionToString, parsePrintColorOptions, parsePrintAreaSizeOptions } from '@customedge/shared';
import type { FabricCanvas } from '../../types/fabric';
import { addImageFitted, scaleToPreserveFootprint } from '../../lib/canvasObjectHelpers';
import { safeGetItem } from '../../lib/safeStorage';
import { useToast, useConfirm } from '../Notifications';
import { translatedColorOptionLabel } from '../../lib/translatedLabels';
import { useDesignStore } from '../../store/designStore';
import { useColorBudget, normalizeColor, rgbCsvToHex, countObjectsUsingColor, replaceColorEverywhere } from '../../hooks/useColorBudget';
import { invalidateThinLineCheck } from '../../hooks/useThinLineCheck';
import { ImageEditPreview, type CropInsets } from './ImageEditPreview';
import { ImageCompareModal } from './ImageCompareModal';
import { Spinner } from '../Spinner';
import { usePopoverPosition, PortaledPopover } from './ColorPicker';
import { nearestPantone, PmsReadout } from './PantoneMatch';
import { InfoTooltip } from './shared/InfoTooltip';

interface StagedImage {
  uploadId: string;
  originalUrl: string;   // pristine upload — never mutated, used by Reset to original
  currentUrl: string;    // what the preview currently shows (result of the latest applied step)
  // Pixel dimensions + embedded DPI of the ORIGINAL upload (all undefined
  // for SVG, which has no fixed pixel resolution/density) — see
  // UploadTool.tsx's StagedImage for the full rationale. `originalDensity`
  // is further undefined whenever the source file itself carries no
  // resolution tag (common for web-exported PNGs — sharp then defaults to
  // 72, which uploads.ts explicitly does NOT paper over, since a bare "72"
  // reads as a real (poor) DPI reading when it's actually just "unknown").
  originalWidth?: number;
  originalHeight?: number;
  originalDensity?: number;
}

// Below this, an image's OWN embedded DPI reads as print-quality-relevant
// rather than incidental — chosen to match this backend's own `config.DPI`
// default (300, see imageProcessing.ts) for "this looks properly
// print-prepared," down to 150 as a conventional large-format-print floor
// before flagging it as worth double-checking. There's no known physical
// print size at this point in the wizard (no design-area size is reliably
// populated anywhere in this codebase today — see IMPLEMENTATION-STATUS.md),
// so this is intentionally just the file's own self-reported density, not an
// "effective DPI at print size" estimate.
function densityQuality(density: number): 'good' | 'low' {
  return density >= 150 ? 'good' : 'low';
}

interface BackgroundColorToolProps {
  canvas: FabricCanvas | null;
  /** The just-uploaded image, held here (not yet on the design canvas) while the wizard runs. */
  staged: StagedImage;
  /** Called once the wizard finishes (or is dismissed) — clears the staged image in UploadTool. */
  onDone: () => void;
  // When set, the wizard is re-correcting an image that's ALREADY a real
  // Fabric object on the canvas (opened from PropertyPanel.tsx's "Correct
  // Colors" button, always staged from that object's own pristine
  // originalUploadUrl — see ImageCorrectionModal.tsx). commitToCanvas swaps
  // this object's own image element in place instead of adding a new
  // object via addImageFitted, so the user's existing position/scale/
  // rotation/layer-order survive untouched — closing the wizard without
  // reaching the end (any point before this runs) leaves `existingObject`
  // completely unmodified, since nothing here mutates it until commit.
  existingObject?: any;
}

// 'removeBackground' and 'removeColor' — previously two separate wizard
// steps — are now ONE step with an internal mode toggle (see `bgOrColorMode`
// below), per direct user request. `selectColors` stays a separate step:
// unlike background/single-color removal, it's not something a user would
// want to do INSTEAD of the other two, so merging it in would force a 3-way
// choice into what's meant to be a simple default-with-an-escape-hatch
// toggle. `selectColors` itself replaced the earlier "Select Single Color"
// step in place (rather than being inserted as a 5th step) per later direct
// user request — picking a 1-color limit in the new step already covers
// what the old single-flatten-color step did, and a color LIMIT (1-4, with
// per-swatch editing) is the more general version of "flatten to one color."
type WizardStep = 'vectorize' | 'removeBackgroundOrColor' | 'selectColors' | 'cropRotate';

const STEP_ORDER: WizardStep[] = ['vectorize', 'removeBackgroundOrColor', 'selectColors', 'cropRotate'];

const DEFAULT_CROP: CropInsets = { top: 0, right: 0, bottom: 0, left: 0 };

// Admin-configurable (ProductConfig.printColorOptions, resolved through
// `?printColors=` — see useProductSetup.ts) replacement for what used to be
// a hardcoded [1,2,3,4]. FULL_COLOR ('full-color') is not a quantization
// limit at all — picking it skips the quantize-colors API call entirely,
// passing every color in the design through to print unchanged (see
// selectFullColor below). A numeric option behaves exactly as the original
// fixed 1-4 buttons did.
type ColorLimit = number;

async function fetchPrintableColors(): Promise<PrintableColor[]> {
  const r = await fetch('/api/printable-colors');
  const d = await r.json();
  return (d.colors ?? []) as PrintableColor[];
}

function hexToRgb(hex: string): { r: number; g: number; b: number } {
  const h = hex.startsWith('#') ? hex : `#${hex}`;
  return {
    r: parseInt(h.slice(1, 3), 16) || 0,
    g: parseInt(h.slice(3, 5), 16) || 0,
    b: parseInt(h.slice(5, 7), 16) || 0,
  };
}

// RGB(0-255) -> CMYK(0-100 ints), same convention PrintableColor.c/m/y/k
// already store (see printableColors admin CSV import) — used to populate
// the swatch editor's CMYK fields from whatever hex the swatch currently is.
function rgbToCmyk(r: number, g: number, b: number): { c: number; m: number; y: number; k: number } {
  if (r === 0 && g === 0 && b === 0) return { c: 0, m: 0, y: 0, k: 100 };
  const rf = r / 255, gf = g / 255, bf = b / 255;
  const k = 1 - Math.max(rf, gf, bf);
  const c = (1 - rf - k) / (1 - k);
  const m = (1 - gf - k) / (1 - k);
  const y = (1 - bf - k) / (1 - k);
  return { c: Math.round(c * 100), m: Math.round(m * 100), y: Math.round(y * 100), k: Math.round(k * 100) };
}

function cmykToHex(c: number, m: number, y: number, k: number): string {
  const kf = k / 100;
  const r = Math.round(255 * (1 - c / 100) * (1 - kf));
  const g = Math.round(255 * (1 - m / 100) * (1 - kf));
  const b = Math.round(255 * (1 - y / 100) * (1 - kf));
  return `#${[r, g, b].map(v => Math.max(0, Math.min(255, v)).toString(16).padStart(2, '0')).join('')}`;
}

// Shared "label, or a real spinner + label while busy" content for every
// processing button in this wizard — per direct user request, replacing
// the previous plain "Converting…"/"Removing…"/etc. text-only busy state
// with an actual spinner. Text-only was easy to miss, especially now that
// vectorize alone can take 15-25+ seconds (see MASK_SUPERSAMPLE_SCALE in
// imageProcessing.ts) — a static label sitting there for that long reads as
// "did my click even register?" rather than "this is actively working."
function ButtonLabel({ busy, label }: { busy: boolean; label: string }) {
  if (!busy) return <>{label}</>;
  return (
    <span style={{ display: 'inline-flex', alignItems: 'center', justifyContent: 'center', gap: 6 }}>
      <Spinner size={12} />
      {label}
    </span>
  );
}

// Shown inside the Images tab (UploadTool.tsx) in place of the drop-zone,
// from the moment a file finishes uploading until the wizard completes and
// the result is placed on the design canvas.
//
// A strict linear wizard, per direct user request ("I want you to do this
// action as step by step"): only the current step's controls are shown; each
// step is answered with either its action ("Apply"/a color swatch) or "Skip
// this" (renamed from "Keep Original" per later user request) before the
// next step unlocks.
//
// IMPORTANT — staged, not live: unlike the pre-rework version, none of this
// touches a Fabric canvas object. Every step operates on the STAGED image
// (`staged.currentUrl`, held as local state / lifted into UploadTool) via the
// same backend endpoints as before (they only ever needed an uploaded-image
// id, never a Fabric object) — the edited image is placed on the canvas via
// `addImageFitted` exactly once, when the wizard finishes (see `commitToCanvas`).
export function BackgroundColorTool({ canvas, staged, onDone, existingObject }: BackgroundColorToolProps) {
  const { t } = useTranslation();
  const showToast = useToast();
  const confirm = useConfirm();
  // Convert-to-Vector and Remove-Background (background mode) both reject
  // SVG uploads server-side (`imageProcessing.ts` — "already a vector" /
  // "not supported for vector uploads") since there's no raster background
  // to trace/flood-fill on a vector document in the first place. Previously
  // the wizard had no idea, so both steps presented a normal-looking button
  // that always failed with a generic error toast — reported live as "1st
  // and 2nd step failed for svg." `staged.originalUrl` always carries the
  // real extension the upload endpoint assigned (`.svg` for a real SVG,
  // confirmed via `uploads.ts`'s `ext` resolution), so this is a reliable,
  // zero-extra-request signal — no new prop/API call needed just to know
  // this up front. "Remove a Color" mode within step 2 is UNAFFECTED (its
  // own endpoint, `/remove-color`, has no such guard and works fine against
  // a rasterized SVG), so only the background-mode button is still gated
  // within step 2 — see modeBackgroundNotApplicableSvg below.
  const isSvgUpload = staged.originalUrl.toLowerCase().endsWith('.svg');

  // Admin-configured (+ `?printColors=`-narrowed) print-color choices for
  // this product — see ProductConfig.printColorOptions / useProductSetup.ts.
  // Falls back to the original fixed 1-4 behavior for a product the admin
  // hasn't configured this on yet (undefined productConfig, e.g. this tool
  // rendered outside the normal designer boot flow).
  //
  // The CURRENTLY ACTIVE SIDE's own printColorOptions override (set per-side
  // by an admin, or backfilled by a vendor loader from that print area/
  // method's own real color-count data — e.g. PFConcept's maxColours differs
  // per method: Screen printing=4, Embroidery=12, Transfer=1) takes priority
  // over the product-wide list when present, so switching the Design tab's
  // active panel to a different print method narrows this step to what that
  // method actually supports, instead of always offering the widest set seen
  // anywhere on the product. Undefined override (the common case today, no
  // vendor loader had run this backfill until this fix) falls through to the
  // product-wide value unchanged.
  const activeSide = useDesignStore(s => s.sides.find(side => side.sideKey === s.activeSideKey));
  // Colors already used elsewhere in the design (this side's active
  // print-color count) — offered as a "Match design colors" quick action in
  // the Select Colors step below, so a newly-placed clipart/image/artwork
  // can be recolored to reuse the design's existing palette instead of
  // introducing new colors that might push the design over budget. Sourced
  // from the SAME scope useColorBudget always uses (active side + active
  // count, not the whole design).
  //
  // Excludes `existingObject`'s OWN current `printColors` when this wizard
  // was reopened via PropertyPanel's "Correct Colors" button (re-editing an
  // already-placed, already-quantized image/clipart/artwork/shape) — without
  // this, that object's own prior palette would count against itself in the
  // budget, artificially shrinking the room available to re-quantize/match
  // it (e.g. a 2-color budget with this exact object already using both
  // slots would read as "at limit" even though re-picking the SAME 2 colors,
  // or fewer, is obviously fine). A freshly-staged upload (existingObject
  // unset) has nothing on canvas yet, so nothing to exclude.
  const designColorBudget = useColorBudget(activeSide, existingObject?.printColors);
  // designColorBudget.usedColors mixes "#hex" and "rgba(r,g,b,a)" entries
  // (whichever a given color object's own value originally was — see
  // useColorBudget's own collectObjectColors) — the backend's targetPalette
  // param strictly requires 6-digit hex, so every entry is normalized to hex
  // here, once, rather than each render/callback re-deriving its own partial
  // (and, previously, buggy — see the fix note below) filtered view.
  const designColorHexPalette = Array.from(new Set(
    designColorBudget.usedColors
      .map(c => c.startsWith('#') ? c : (normalizeColor(c) ? rgbCsvToHex(normalizeColor(c)!) : null))
      .filter((c): c is string => !!c),
  ));
  const sideColorOptionsOverride = activeSide?.printColorOptions;
  const productConfig = useDesignStore(s => s.productConfig);
  const productColorOptions = productConfig?.printColorOptions ?? DEFAULT_PRINT_COLOR_OPTIONS;
  const printColorOptions = sideColorOptionsOverride ? parsePrintColorOptions(sideColorOptionsOverride) : productColorOptions;
  // Print-AREA-SIZE tiers (cm2) for the currently active side's method --
  // e.g. PFConcept's DTF Transfer, which has NO real color-count concept
  // (its vendor feed's own maxColours is "full color") but DOES have real
  // physical-size tiers (see printAreaSizes.ts / ImageSideConfig.
  // printAreaSizeOptions's schema comment).
  //
  // UNLIKE printColorOptions above, this deliberately does NOT fall back to
  // the product-wide list when the active side has no override -- a
  // product-wide fallback is correct for printColorOptions (every method on
  // a product plausibly shares one color-count ceiling), but area-size
  // tiers are per-METHOD, not a product-wide concept: a product with a
  // DTF-Transfer area (has real tiers) and a Zeefdruk area (color-count
  // only, no tiers at all) would otherwise show DTF Transfer's tiers on the
  // Zeefdruk side too, since printConfig.printAreaSizeOptions is a UNION
  // across the whole product (see load-vendor-pfconcept.ts's own product-
  // level computation). Confirmed live 2026-08-07 on PX120181: Zeefdruk
  // side has no printAreaSizeOptions override, but the product-wide union
  // (from its sibling DTF Transfer side) is [100,300,500,750] -- with a
  // fallback, Zeefdruk's Select Colors step wrongly showed those 4 size
  // tiers instead of its own real 1-4 color-count buttons. Only a side
  // with its OWN real override is treated as having tiers.
  const sideAreaSizeOptionsOverride = activeSide?.printAreaSizeOptions;
  const printAreaSizeOptions = sideAreaSizeOptionsOverride
    ? parsePrintAreaSizeOptions(sideAreaSizeOptionsOverride)
    : [];
  const hasAreaSizeTiers = printAreaSizeOptions.length > 0;
  // The currently-effective print method, for resolving this step's color
  // chips through the Magento-translated maps (see translatedColorOptionLabel)
  // — same "single agreed value or none" rule DesignerHeader.tsx uses for its
  // own pills. `DesignSide` (this store's canvas-facing side type) doesn't
  // carry printMethod itself, so this reads the customer's own selection
  // instead of the side's tag.
  const selectedPrintMethod = useDesignStore(s => s.selectedPrintMethod);
  const selectedPrintPositions = useDesignStore(s => s.selectedPrintPositions);
  const selectedPrintMethodByPosition = useDesignStore(s => s.selectedPrintMethodByPosition);
  const activeMethodForColorTranslation = selectedPrintPositions.length > 0
    ? (selectedPrintPositions.every(p => selectedPrintMethodByPosition[p] === selectedPrintMethodByPosition[selectedPrintPositions[0]])
        ? selectedPrintMethodByPosition[selectedPrintPositions[0]]
        : undefined)
    : selectedPrintMethod ?? undefined;
  // "Only Full Color is offered" -> there's nothing to choose between, so the
  // whole step is skipped rather than shown with a single, always-selected
  // button — same reasoning as vectorize being dropped entirely for SVG
  // uploads below, not just disabled. EXCEPT when this method has real
  // area-size tiers (hasAreaSizeTiers) -- that's exactly the "Full Color
  // only" shape in printColorOptions terms (no color-count concept), but
  // there IS a real choice to make here (which size tier), so the step
  // must still show its area-size branch rather than being skipped.
  const skipSelectColorsStep = printColorOptions.length === 1 && printColorOptions[0] === FULL_COLOR && !hasAreaSizeTiers;

  // Step 1 (Convert to Vector) is skipped entirely, not just disabled, for
  // SVG uploads — an upload that's ALREADY a vector has nothing for this
  // step to do (see the 400 "already a vector" guard server-side); showing
  // it with a "not applicable" message still cost an extra click just to
  // dismiss. Per direct user request ("If SVG image as input why still you
  // are showing 1st step... auto skip that step and move to step 2"), the
  // step is removed from the wizard's own step order for this upload
  // entirely, rather than rendered-then-skipped. `stepIndex`/goToNextStep/
  // goToPreviousStep/startOver all index into THIS array (never the raw
  // STEP_ORDER constant directly) so "step 1" genuinely means step 2's
  // content for an SVG upload — the progress dots/count also naturally
  // shrink to 3 instead of 4.
  const effectiveStepOrder = STEP_ORDER.filter(s => {
    if (s === 'vectorize' && isSvgUpload) return false;
    if (s === 'selectColors' && skipSelectColorsStep) return false;
    return true;
  });
  const [busy, setBusy] = useState<'vectorize' | 'remove' | 'detect' | 'recolor' | 'removeColor' | 'crop' | 'placing' | null>(null);
  const [stepIndex, setStepIndex] = useState(0);
  // Distinct from `stepIndex` reaching the last entry: that state still shows
  // the final (Crop/Rotate) step's own controls. This flips true only once
  // that step itself has been answered (Apply or Skip this), so the wizard
  // can show a clear "all steps done" end state instead of leaving it
  // visibly re-clickable forever with no indication anything finished.
  const [wizardDone, setWizardDone] = useState(false);
  const [colors, setColors] = useState<string[]>([]);
  const [colorsLoadedForStep, setColorsLoadedForStep] = useState<WizardStep | null>(null);
  // The merged step's internal choice — defaults to background removal per
  // direct user request ("by default give background removal, if user need
  // they will switch to option color removal"). 'color' reveals the same
  // detected-swatches UI the old separate "Remove Selected Color" step had.
  // Exception: SVG uploads default straight to 'color', since background-mode's
  // /remove-background endpoint rejects vector input server-side — defaulting
  // to 'background' for an SVG would always land on the one option that's
  // guaranteed to fail (see isSvgUpload above).
  const [bgOrColorMode, setBgOrColorMode] = useState<'background' | 'color'>(
    staged.originalUrl.toLowerCase().endsWith('.svg') ? 'color' : 'background',
  );
  // Which specific swatch is mid-request — `busy` alone ('removeColor' /
  // 'recolor') identifies the OPERATION but not which of several same-
  // colored-mode swatches was actually clicked, so without this every
  // swatch would show a spinner at once instead of just the clicked one.
  const [busyHex, setBusyHex] = useState<string | null>(null);
  // "Select Colors" step (selectColors) — which of the admin-configured
  // options is picked: a flat-color-count limit (ColorLimit/number) or
  // FULL_COLOR ('full-color', meaning "skip quantization, pass every color
  // through unchanged" — see selectFullColor below). `?printColor=` (see
  // useProductSetup.ts's getRequestedPrintColorFromSearch) — a single explicit
  // pre-selection distinct from `?printColors=`'s whole-SET narrowing above —
  // wins here if it resolved to one of the options actually on offer, so a
  // launch URL requesting print color "3" both narrows the header pill AND
  // is what this step auto-applies, instead of silently defaulting to the
  // max regardless.
  //
  // The CUSTOMER'S OWN per-position pick (selectedPrintColorByPosition, set
  // via the header's print-color popup — see DesignerHeader.tsx's
  // rawColorForPosition) takes priority over that flat/URL value when the
  // active side has a position with its own entry, same "per-position
  // overrides flat" precedent effectiveMethodFor (useProductSetup.ts) and
  // DesignerHeader's own rawMethodForPosition/rawColorForPosition already
  // follow. Without this, a user who picked e.g. "2 colors" for this side's
  // position in the header still landed on this step defaulted to the
  // highest configured limit (or a DIFFERENT position's/the flat value),
  // requiring a manual re-pick here to match what they'd already chosen —
  // reported live as the Select Colors step "not remembering" the header's
  // print color choice.
  const numericPrintColorOptions = printColorOptions.filter((o): o is number => o !== FULL_COLOR);
  const requestedPrintColorOption = useDesignStore(s => s.selectedPrintColorOption);
  const selectedPrintColorByPosition = useDesignStore(s => s.selectedPrintColorByPosition);
  const positionPrintColorOption = activeSide?.printPosition
    ? selectedPrintColorByPosition[activeSide.printPosition]
    : undefined;
  const effectiveRequestedPrintColorOption = positionPrintColorOption ?? requestedPrintColorOption;
  const defaultColorOption: ColorLimit | typeof FULL_COLOR =
    effectiveRequestedPrintColorOption != null && printColorOptions.includes(effectiveRequestedPrintColorOption)
      ? effectiveRequestedPrintColorOption
      : numericPrintColorOptions.length ? Math.max(...numericPrintColorOptions) : FULL_COLOR;
  const [selectedColorOption, setSelectedColorOption] = useState<ColorLimit | typeof FULL_COLOR>(defaultColorOption);
  // Which area-size tier (cm2) is picked, when hasAreaSizeTiers is true —
  // defaults to the SMALLEST tier (cheapest/most conservative default,
  // mirroring how a customer would expect "pick a print size" to start
  // small rather than defaulting to the largest/most expensive option the
  // numeric color-count picker above defaults to). Selecting a tier never
  // triggers quantize-colors -- these methods are always full-color (see
  // hasAreaSizeTiers's own comment), so this behaves like selectFullColor:
  // a no-op quantization, just records the choice.
  const [selectedAreaSizeOption, setSelectedAreaSizeOption] = useState<number | null>(
    hasAreaSizeTiers ? Math.min(...printAreaSizeOptions) : null,
  );
  // Gates the size-tier branch's own "Done selecting size" commit button —
  // deliberately a DEDICATED flag rather than the shared `hasEdits` (which
  // this step's tier click also sets, but which earlier wizard steps
  // (vectorize/removeBackground/etc, see their own setHasEdits(true) calls)
  // can already have set true before the user ever reaches this step,
  // showing the button prematurely on step entry. Only an actual click on a
  // tier button below should reveal it.
  const [areaSizeEdited, setAreaSizeEdited] = useState(false);
  const [convertEnabled, setConvertEnabled] = useState(false);
  const [quantizedPalette, setQuantizedPalette] = useState<string[]>([]);
  // "Background color" toggle — a flat fill counted as ONE extra print color
  // behind this image, independent of the N-color foreground quantization
  // above (mirrors the reference tool's separate "Background color" section
  // in its Edit Colors panel). Purely frontend/print-metadata: unlike the
  // foreground palette, there's no backend quantize call for this — it never
  // touches the staged image's own pixels, it's an additional flat color the
  // print spec needs to account for (e.g. a colored garment/substrate showing
  // through a transparent background left by the Remove Background step).
  // Defaults to the image's own average edge color the first time it's
  // switched on (see enableBackgroundColor), then behaves exactly like any
  // other swatch (editable via the same SwatchEditorPopover).
  const [backgroundColorEnabled, setBackgroundColorEnabled] = useState(false);
  const [backgroundColorHex, setBackgroundColorHex] = useState('#e5c94f');
  const [editingBackgroundColor, setEditingBackgroundColor] = useState(false);
  // Maps a CURRENT swatch hex -> the override applied to it (a new hex, or
  // null for "marked transparent") — keyed by current hex rather than an
  // index so it stays correct as quantizedPalette itself gets replaced by
  // each successive /recolor-swatch response.
  const [swatchOverrides, setSwatchOverrides] = useState<Record<string, string | null>>({});
  const [editingSwatch, setEditingSwatch] = useState<string | null>(null);
  // Hex of whichever quantized-palette (or background-color) swatch is
  // currently under the pointer — lifted up so ImageEditPreview can dim
  // every non-matching pixel in the live preview while it's set, per direct
  // user request ("On hover of the Color highlight the specific color in
  // image preview"). Cleared on pointer-leave; never set while a swatch's
  // own editor popover is open (see the SwatchWithEditor hover wiring below)
  // so the preview doesn't keep highlighting a color the user has already
  // moved on to editing.
  const [hoveredSwatchHex, setHoveredSwatchHex] = useState<string | null>(null);
  const { data: printableColors = [] } = useQuery({
    queryKey: ['printable-colors'],
    queryFn: fetchPrintableColors,
    staleTime: 300_000,
  });
  const [cropInsets, setCropInsets] = useState<CropInsets>(DEFAULT_CROP);
  const [rotation, setRotation] = useState(0);
  // Opens the larger zoomable original-vs-current comparison — see
  // ImageCompareModal.tsx. Available on every step (not just after an edit),
  // so a user can always double-check what the image looked like before
  // touching it.
  const [compareOpen, setCompareOpen] = useState(false);
  // Tracks whether ANY edit (any step's action, not "Keep Original") has been
  // applied yet — gates the always-visible "Reset to original" button, which
  // must cover every step, not just background removal.
  const [hasEdits, setHasEdits] = useState(false);
  // Current preview URL — starts as the pristine upload, updated by each
  // step's Apply. This useState initializer only runs once, on mount — it
  // does NOT re-sync if `staged` changes on a later render, which is
  // exactly why UploadTool.tsx mounts this component with `key={staged.uploadId}`:
  // that key forces a full remount (fresh useState calls) whenever a
  // DIFFERENT image is staged, rather than silently reusing this instance's
  // stale wizard state (previewUrl, stepIndex, wizardDone, colors, ...) for
  // a new image. Without that key, uploading a second image mid-wizard on
  // the first left every one of these useState values frozen at the first
  // image's values while the backend calls below started targeting the new
  // image's uploadId — confirmed live as the reported "1st image cached,
  // replaced next image while processing" bug.
  const [previewUrl, setPreviewUrl] = useState(staged.currentUrl);
  // Snapshot of `previewUrl` from the MOMENT selectColors becomes active,
  // before any quantize-colors call (auto or manual) has had a chance to
  // overwrite it — i.e. "what steps 1-2 actually produced." Used to restore
  // `previewUrl` when the user picks Full Color, since that choice has no
  // backend call of its own to hand back a fresh URL (see selectFullColor).
  // Without this, choosing Full Color AFTER a numeric option had already run
  // (including the auto-quantize-to-the-default-option effect below, which
  // fires before the user touches anything) left `previewUrl` pointing at
  // that quantized/flattened result — the wrong (color-limited) image ends
  // up committed to canvas despite "Volledige kleur" being the one visibly
  // selected. Reported live as "Full color not working... wrong image ends
  // up on canvas."
  const preQuantizePreviewUrlRef = useRef<string | null>(null);
  // Survives `resetColorLimitState()` — unlike `quantizedPalette`/`convertEnabled`,
  // which that function unconditionally clears on EVERY step transition (Back/
  // Skip/Next all funnel through it, not just leaving selectColors), this
  // captures "the palette actually chosen for this image" at the one moment it's
  // known (right before selectColors's own state gets wiped by advancing/going
  // back past it) and holds it for `commitToCanvas` to read later, once the user
  // reaches the final Place-on-Design screen — by which point `quantizedPalette`
  // has long since been reset to `[]` regardless of what the user picked.
  // Without this, `commitToCanvas`'s own `convertEnabled && quantizedPalette.length > 0`
  // condition could never be true in practice (every path to the end of the
  // wizard passes through at least one `goToNextStep()`/`resetColorLimitState()`
  // after leaving selectColors), so the Design Guide PDF always fell back to
  // "Full color — no color limit applied" even when the customer had picked a
  // real numeric color limit and it was correctly quantized on-screen. Reported
  // live: customer selected "2" in Select Colors, quantized image showed 2
  // colors in the wizard preview, yet the printing team's Design Guide PDF still
  // said Full Color. `null` = no numeric quantization is currently the
  // step's answer (Full Color chosen, or the step was skipped/never reached) —
  // distinct from an empty array, which would incorrectly still satisfy a bare
  // `!= null` check downstream if this held `[]` for "not applicable."
  const committedPrintColorsRef = useRef<string[] | null>(null);
  // Guards the auto-quantize effect below against firing twice for the same
  // step-entry. `busy` state alone isn't enough: React 18 StrictMode
  // double-invokes effects synchronously (mount -> cleanup -> mount again)
  // before the first invocation's `setBusy('recolor')` has committed/
  // re-rendered, so both invocations can read `busy === null` and both fire
  // a real POST /quantize-colors (or /remove-background via selectFullColor's
  // caller) for the same upload — confirmed live via Playwright network
  // logging: two requests fired ~175ms apart, both got 200s. A ref updates
  // synchronously within the same tick, unlike state, so it closes this race.
  // Reset alongside every other per-step-entry ref/state (resetColorLimitState
  // already runs on every Back/Skip/Next/startOver/resetToOriginal path).
  const autoQuantizeFiredRef = useRef(false);
  // Same StrictMode double-invoke race as autoQuantizeFiredRef above, for the
  // detectColorsForStep auto-fetch effect further down — `colorsLoadedForStep`/
  // `busy` are both state, so a second synchronous effect invocation can still
  // read the pre-update values and fire a second POST /palette for the same
  // step. Keyed by step (not a bare boolean) since unlike auto-quantize this
  // effect legitimately needs to fire again for a genuinely different step.
  const colorsFetchInFlightForStepRef = useRef<WizardStep | null>(null);
  // Color-distance threshold the backend's flood-fill uses to decide what
  // counts as "background" — settled at these values across earlier testing
  // ("Sensitivity: 30, Clear small gaps... always select and both is giving
  // good result"), not exposed as adjustable controls per user request.
  const tolerance = 30;
  const clearEnclosedGaps = true;

  const step = effectiveStepOrder[stepIndex];
  // 1-based position of a given step WITHIN effectiveStepOrder — every step
  // title (e.g. "2. Remove Background or Color") interpolates this instead
  // of a hardcoded number, since a step's real position shifts for an SVG
  // upload (Convert to Vector is removed from effectiveStepOrder entirely,
  // so "Remove Background or Color" becomes step 1, not 2, in that case).
  const stepNumberFor = (s: WizardStep): number => effectiveStepOrder.indexOf(s) + 1;

  const authHeaders = (): HeadersInit => {
    const token = safeGetItem('session', 'customedge_token');
    return token ? { Authorization: `Bearer ${token}` } : {};
  };

  const resetColorLimitState = () => {
    setConvertEnabled(false);
    setQuantizedPalette([]);
    setSwatchOverrides({});
    setEditingSwatch(null);
    setBackgroundColorEnabled(false);
    setEditingBackgroundColor(false);
    // Cleared on every exit from selectColors (Back/Skip/Next/startOver/
    // resetToOriginal all call this) so re-entering the step later captures
    // a fresh preQuantizePreviewUrlRef snapshot instead of reusing one from
    // a previous visit — see that ref's own comment.
    preQuantizePreviewUrlRef.current = null;
    autoQuantizeFiredRef.current = false;
    // Every call site of this function also calls setColorsLoadedForStep(null)
    // (or, for the mode-toggle site below, changes bgOrColorMode) — either way
    // the colors-fetch effect is about to legitimately re-run for a step it
    // hasn't fetched for yet, so the in-flight marker from whatever step is
    // being left must not block it.
    colorsFetchInFlightForStepRef.current = null;
  };

  // Snapshots the selectColors step's own answer into committedPrintColorsRef
  // right before resetColorLimitState() wipes quantizedPalette/convertEnabled
  // — see that ref's own comment for why this snapshot has to happen HERE,
  // not read later from the (by-then-cleared) state. Only meaningful while
  // selectColors is the CURRENT step being left; every other step transition
  // (e.g. leaving cropRotate) must leave a previously-committed value alone.
  const commitColorLimitIfLeavingSelectColors = () => {
    if (step !== 'selectColors') return;
    const foreground =
      convertEnabled && selectedColorOption !== FULL_COLOR && quantizedPalette.length > 0
        ? quantizedPalette
        : null;
    committedPrintColorsRef.current = backgroundColorEnabled
      ? [...(foreground ?? []), backgroundColorHex]
      : foreground;
  };

  const goToNextStep = () => {
    commitColorLimitIfLeavingSelectColors();
    setColors([]);
    setColorsLoadedForStep(null);
    resetColorLimitState();
    setStepIndex(i => {
      if (i >= effectiveStepOrder.length - 1) {
        setWizardDone(true);
        return i;
      }
      return i + 1;
    });
  };

  const goToPreviousStep = () => {
    commitColorLimitIfLeavingSelectColors();
    setColors([]);
    setColorsLoadedForStep(null);
    resetColorLimitState();
    setStepIndex(i => Math.max(i - 1, 0));
  };

  // Re-enters the wizard from step 1 WITHOUT discarding the currently staged
  // preview — lets a user run the steps again (e.g. crop tighter, or try
  // removing a second color) without starting over from the pristine upload.
  const startOver = () => {
    setWizardDone(false);
    setStepIndex(0);
    setColors([]);
    setColorsLoadedForStep(null);
    setBgOrColorMode('background');
    resetColorLimitState();
    committedPrintColorsRef.current = null;
    setSelectedColorOption(defaultColorOption);
    setCropInsets(DEFAULT_CROP);
    setRotation(0);
  };

  // Step 1 — Traces the image into an SVG (potrace) before any background
  // removal/color work, so those later steps can operate on smoother
  // vector-traced edges instead of the raw upload's compression noise/
  // anti-aliasing. Best suited to flat-color logos/line art — a photographic
  // upload will come out looking like a posterized stencil; "Reset to
  // original" is the escape hatch, or simply choosing "Keep Original" here.
  const vectorize = async () => {
    setBusy('vectorize');
    try {
      const res = await fetch(`/api/images/${staged.uploadId}/vectorize`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', ...authHeaders() },
        body: JSON.stringify({}),
      });
      if (!res.ok) throw new Error(await res.text());
      const { url } = await res.json();
      setPreviewUrl(url);
      setHasEdits(true);
      goToNextStep();
    } catch {
      showToast(t('bgColor.vectorizeFailed'), 'error');
    } finally {
      setBusy(null);
    }
  };

  // Step 2 ('background' mode of the merged removeBackgroundOrColor step) —
  // Border-connected flood-fill background removal. Omitting `source` lets
  // the backend prefer the vectorized result over the raw original on its
  // own (readBestSourceRaster in imageProcessing.ts).
  const removeBackground = async () => {
    setBusy('remove');
    try {
      const res = await fetch(`/api/images/${staged.uploadId}/remove-background`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', ...authHeaders() },
        body: JSON.stringify({ tolerance, clearEnclosedGaps }),
      });
      if (!res.ok) throw new Error(await res.text());
      const { url } = await res.json();
      setPreviewUrl(url);
      setHasEdits(true);
      goToNextStep();
    } catch {
      showToast(t('bgColor.removeFailed'), 'error');
    } finally {
      setBusy(null);
    }
  };

  // Shared by the merged step's color mode and the selectColors step, but
  // NOT with the same source semantics — the two need genuinely different
  // answers to "what colors are there":
  //   - removeBackgroundOrColor (color mode): must show the colors
  //     remove-color is actually about to operate on, i.e. the stable step-1
  //     output (vectorized/original) — matches remove-color's own now-fixed
  //     'vectorized' resolution (see that route's comment). Explicitly passes
  //     source=vectorized so re-entering this step via Back always shows the
  //     TRUE original palette to choose from again, not an already-reduced
  //     one left over from a previous pass's removal.
  //   - selectColors: wants the CURRENT/cumulative palette (whatever steps
  //     1-2 actually produced) as its "colors currently in the design"
  //     readout — omits source, keeping the default latest.png-first lookup.
  const detectColorsForStep = async (forStep: WizardStep) => {
    setBusy('detect');
    try {
      const query = forStep === 'removeBackgroundOrColor' ? '?source=vectorized' : '';
      const res = await fetch(`/api/images/${staged.uploadId}/palette${query}`, {
        method: 'POST',
        headers: authHeaders(),
      });
      if (!res.ok) throw new Error(await res.text());
      const { colors: detected } = await res.json();
      setColors(detected ?? []);
      setColorsLoadedForStep(forStep);
    } catch {
      showToast(t('bgColor.detectFailed'), 'error');
      // Mark this step "loaded" (with an empty palette) even on failure —
      // colorsLoadedForStep is the auto-fetch effect's ONLY guard against
      // re-running (see that effect's own comment); leaving it null after a
      // failure looked harmless (busy still gets reset in `finally` below)
      // but every `setBusy(null)` is itself a dependency of that effect, so
      // it re-ran, re-failed, and re-toasted forever with no backoff — a
      // template-placed image (no real backend UploadedImage row behind its
      // synthesized uploadId — see ImageCorrectionModal.tsx's fallback)
      // 404s every single attempt, which is exactly what surfaced this as a
      // toast flood rather than a single error. One failed attempt per
      // step-entry is enough; a real retry now requires leaving and
      // re-entering the step (Back/Next), same as any other failed action
      // in this wizard.
      setColorsLoadedForStep(forStep);
    } finally {
      setBusy(null);
      if (colorsFetchInFlightForStepRef.current === forStep) colorsFetchInFlightForStepRef.current = null;
    }
  };

  // Auto-loads swatches the moment a step/mode that needs them becomes
  // active, instead of requiring an explicit "Detect Colors" click — per
  // direct user request ("load the color automatically while entering the
  // step and remove the button"). Runs for: the merged step while in color
  // mode, and the selectColors step. Guards against re-fetching if colors are
  // already loaded for the currently active step/mode (colorsLoadedForStep
  // is reset to null by goToNextStep/goToPreviousStep whenever the step
  // actually changes, and by the mode toggle below when switching within
  // the merged step) and against firing while something else is mid-request.
  useEffect(() => {
    const needsColors =
      (step === 'removeBackgroundOrColor' && bgOrColorMode === 'color') ||
      step === 'selectColors';
    if (!needsColors) return;
    if (colorsLoadedForStep === step) return;
    if (busy !== null) return;
    if (colorsFetchInFlightForStepRef.current === step) return;
    colorsFetchInFlightForStepRef.current = step;
    detectColorsForStep(step);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [step, bgOrColorMode, colorsLoadedForStep, busy]);

  // Captures preQuantizePreviewUrlRef the instant selectColors becomes
  // active, BEFORE the auto-quantize effect below (or any manual click) has
  // a chance to overwrite `previewUrl` with a quantized result — this must
  // run first, so it's declared before that effect (effects run in
  // declaration order within one render). Guarded the same way
  // `colorsLoadedForStep`/`detectColorsForStep` already are, via a ref rather
  // than state so setting it never itself triggers a re-render/extra effect
  // pass. Reset on step re-entry (goToNextStep/goToPreviousStep/startOver all
  // already reset colorsLoadedForStep to null on leaving this step, which
  // this mirrors) so re-entering selectColors after Back captures a fresh
  // snapshot rather than reusing a stale one from a previous visit.
  useEffect(() => {
    if (step !== 'selectColors') return;
    if (preQuantizePreviewUrlRef.current !== null) return;
    preQuantizePreviewUrlRef.current = previewUrl;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [step]);

  // Auto-quantizes to the default color option the instant selectColors
  // becomes active — without this, the default button renders as SELECTED
  // even though nothing was actually quantized yet, since smartQuantizeColors
  // only ever fired from an explicit click. Reported live as two symptoms of
  // the same root cause: "4 colors tiles not loading" (quantizedPalette empty
  // despite "4" looking active) and, when steps 1-2 were skipped first, "I
  // can see the old image" (previewUrl never left the pristine upload,
  // because no step had actually applied anything yet — correct for a
  // genuinely untouched image, but confusing paired with a color-limit button
  // that LOOKS pre-selected). Guarded on `!convertEnabled` so this only fires
  // once per step-entry, not after every subsequent manual click (those
  // already call smartQuantizeColors directly) or after Back/Skip reset the
  // step state. If the default resolved to FULL_COLOR (see
  // selectedColorOption's own comment), there's nothing to auto-apply —
  // selectFullColor sets `convertEnabled` itself so this effect's guard
  // prevents it firing twice.
  //
  // Uses smartQuantizeColors (reserve-existing-design-colors-then-fill-from-
  // image), per direct user request that entering this step should
  // auto-include whatever colors the customer already used elsewhere in the
  // design before pulling any new colors from the image — see
  // smartQuantizeColors' own comment for the full spec.
  useEffect(() => {
    if (step !== 'selectColors') return;
    if (convertEnabled) return;
    if (busy !== null) return;
    if (autoQuantizeFiredRef.current) return;
    autoQuantizeFiredRef.current = true;
    if (selectedColorOption === FULL_COLOR) selectFullColor();
    else smartQuantizeColors(selectedColorOption);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [step, convertEnabled, busy]);

  // Step 2 ('color' mode of the merged removeBackgroundOrColor step) —
  // Removes every pixel matching `hex` anywhere in the image (not just a
  // connected region, not just the background) — distinct from
  // removeBackground (border-connected only). Omitting `source` lets the
  // backend pick bg-removed > vectorized > original on its own.
  const removeColor = async (hex: string) => {
    setBusy('removeColor');
    setBusyHex(hex);
    try {
      const res = await fetch(`/api/images/${staged.uploadId}/remove-color`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', ...authHeaders() },
        body: JSON.stringify({ hex }),
      });
      if (!res.ok) throw new Error(await res.text());
      const { url } = await res.json();
      setPreviewUrl(url);
      setHasEdits(true);
      goToNextStep();
    } catch {
      showToast(t('bgColor.removeColorFailed'), 'error');
    } finally {
      setBusy(null);
      setBusyHex(null);
    }
  };

  // Step 3 (selectColors) — Reduces the design to at most `maxColors` flat
  // colors (preserves alpha, so a removed background from step 2 stays
  // transparent). Populates `quantizedPalette` with the resulting swatches
  // for the per-swatch editor below (recolorSwatch) rather than advancing
  // the wizard immediately — unlike every other step's action, this one
  // isn't itself the final answer; the user typically edits a swatch or two
  // afterward before choosing Done/continuing.
  //
  // Shared by smartQuantizeColors below, which in turn is called from both
  // the numeric option buttons' onClick and the auto-quantize-on-entry
  // effect — pulled out once those callers' near-identical response handling
  // (previewUrl/quantizedPalette/swatchOverrides/convertEnabled/hasEdits/
  // busy/error toast) risked drifting apart across multiple call sites.
  //
  // Callers take `maxColors` as an explicit argument rather than reading the
  // `selectedColorOption` state — the option buttons' onClick calls this in
  // the SAME click as `setSelectedColorOption(n)`; reading the state instead
  // would close over whatever it was on the render that created that
  // callback, which is one click BEHIND the number just clicked (React state
  // updates aren't visible until the next render) — confirmed live as the
  // reported "after check the checkbox it replacing image with old image"
  // bug (the checkbox toggle used the stale just-changed limit for whichever
  // number had been clicked most recently, not necessarily the one now
  // highlighted, and — since the backend's quantize-colors defaults to
  // re-reading `latest.png`, i.e. "whatever was quantized last time" — a
  // stale/mismatched maxColors could land on top of an unrelated prior
  // result). Passing the number directly makes the request unambiguous
  // regardless of React's render timing.
  const runQuantize = async (body: { maxColors: number; targetPalette?: string[] }) => {
    setBusy('recolor');
    try {
      const res = await fetch(`/api/images/${staged.uploadId}/quantize-colors`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', ...authHeaders() },
        body: JSON.stringify(body),
      });
      if (!res.ok) throw new Error(await res.text());
      const { url, palette } = await res.json();
      setPreviewUrl(url);
      setQuantizedPalette(palette ?? []);
      setSwatchOverrides({});
      setConvertEnabled(true);
      setHasEdits(true);
    } catch {
      showToast(t('bgColor.quantizeFailed'), 'error');
    } finally {
      setBusy(null);
    }
  };

  // Default auto-fill the instant "Select Colors" is entered (see the
  // useEffect below) — reserves up to `maxColors` slots for colors the
  // customer has ALREADY used elsewhere in the design (text fills, other
  // objects' printColors), then lets the backend fill any remaining slots
  // from this image's own dominant colors. Per direct user request: "custom
  // selected 4 color and already added two text object in 2 different color
  // then Select color section need to auto include the 2 color... If 4 color
  // already selected other object then auto color will use customer already
  // selected 4 color no colors will pick from image." Reserved colors are
  // capped at maxColors (never MORE reserved slots than the budget itself,
  // which can't happen anyway since designColorHexPalette is itself bounded
  // by the same side-wide budget, but sliced defensively here to keep this
  // function's own contract obvious without relying on that invariant
  // holding elsewhere) — see quantize-colors' own backend comment for the
  // actual reserve-then-fill implementation. (An earlier version of this
  // step also offered a separate, explicit "Match design colors" button —
  // an all-or-nothing existing-colors-only recolor — as a manual escape
  // hatch alongside this auto-fill default; removed per direct user
  // request, since this auto-fill already covers the same reserve-existing-
  // colors intent as its default behavior, and having both was redundant.)
  const smartQuantizeColors = (maxColors: ColorLimit) => {
    const reserved = designColorHexPalette.slice(0, maxColors);
    if (reserved.length === 0) runQuantize({ maxColors });
    else runQuantize({ maxColors, targetPalette: reserved });
  };

  // "Full Color" choice — deliberately makes NO backend call: the whole point
  // is that every color in the design passes through to print unchanged, so
  // there's nothing to quantize/flatten. Marks the step answered by reusing
  // `convertEnabled` (gates the "Done editing colors" button below, same as
  // after a real quantize) and shows the design's own already-detected
  // palette (`colors`, auto-loaded for this step — see the detectColorsForStep
  // effect above) as the swatch readout, instead of `quantizedPalette` (which
  // stays empty since quantize-colors never ran).
  //
  // Restores `previewUrl` to preQuantizePreviewUrlRef (whatever steps 1-2
  // actually produced, captured before this step's own auto-quantize or any
  // manual number click could touch it) — Full Color has no fetch response
  // of its own to hand back a fresh URL, so without this, switching TO Full
  // Color after a numeric option had already quantized (including the
  // default-option auto-quantize that runs before the user touches
  // anything) left `previewUrl` pointing at that flattened result. That
  // stale quantized image is exactly what `commitToCanvas` places on the
  // canvas — confirmed live as the reported "Full color not working — wrong
  // image ends up on canvas."
  const selectFullColor = () => {
    if (preQuantizePreviewUrlRef.current !== null) setPreviewUrl(preQuantizePreviewUrlRef.current);
    setQuantizedPalette([]);
    setSwatchOverrides({});
    setConvertEnabled(true);
    setHasEdits(true);
  };

  // Which of quantizedPalette's swatches are "locked" — reserved from the
  // design's OWN already-used colors by smartQuantizeColors' reserve-then-
  // fill (see its own comment), as opposed to a color this step picked from
  // the image's own pixels. A locked swatch isn't just this staged image's
  // own color choice — some OTHER object on the canvas already relies on it,
  // so editing it away has consequences beyond this one image (unlike an
  // unlocked/image-derived swatch, which is purely local to this upload
  // until Place-on-Design commits it). Compared by normalizeColor so an
  // exact-hex match survives hex-vs-rgba formatting differences the same way
  // every other budget comparison in this app does.
  const isLockedSwatch = (hex: string): boolean => {
    const key = normalizeColor(hex);
    return key != null && designColorHexPalette.some(c => normalizeColor(c) === key);
  };

  // Repaints one swatch of the already-quantized palette to a new color, or
  // (when `to` is null) marks it transparent — the reference UI's per-swatch
  // "Pick your color" / "Transparent" controls. Updates `quantizedPalette`
  // in place (dropping the swatch entirely when it's made transparent) so
  // the "Total color count" readout reflects the edit immediately.
  //
  // Locked swatches (see isLockedSwatch) confirm first and, once confirmed,
  // ALSO propagate the change to every other object on this side currently
  // using that color via replaceColorEverywhere — per direct user request
  // ("If user try to change the locked (Used Color) it will give prompt and
  // get confirmation and on change it update the other object used this
  // color"). This is layered on TOP of the existing recolor-swatch call
  // (which only ever repaints THIS staged image's own pixels) rather than
  // replacing it — a locked swatch being edited needs BOTH: this image's
  // pixels AND every other canvas object sharing that color, since neither
  // call touches the other's target.
  //
  // `existingObject` is passed as countObjectsUsingColor's excludeObject —
  // when this wizard is re-editing an already-placed image ("Correct
  // Colors," see this component's own prop comment), that object is STILL
  // on the live canvas with its OLD printColors while this dialog is open,
  // so without excluding it, its own prior use of fromHex would count as a
  // first "other" usage and wrongly confirm on every edit, even ones that
  // genuinely touch nothing else. A freshly-staged upload has no
  // existingObject at all, so nothing to exclude there.
  const recolorSwatch = async (fromHex: string, toHex: string | null) => {
    if (toHex !== null && isLockedSwatch(fromHex) && canvas && activeSide) {
      const { objectCount, usesBackground } = countObjectsUsingColor(canvas, activeSide, fromHex, existingObject);
      const otherUsageCount = objectCount + (usesBackground ? 1 : 0);
      if (otherUsageCount > 0) {
        const ok = await confirm(t('colorsUsed.confirmReplaceBody', { count: otherUsageCount }), { confirmLabel: t('colorsUsed.confirmReplaceButton') });
        if (!ok) return;
      }
    }
    setBusy('recolor');
    setBusyHex(fromHex);
    try {
      const res = await fetch(`/api/images/${staged.uploadId}/recolor-swatch`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', ...authHeaders() },
        body: JSON.stringify({ from: fromHex, to: toHex }),
      });
      if (!res.ok) throw new Error(await res.text());
      const { url } = await res.json();
      setPreviewUrl(url);
      setHasEdits(true);
      setQuantizedPalette(prev => toHex === null ? prev.filter(h => h !== fromHex) : prev.map(h => h === fromHex ? toHex : h));
      setSwatchOverrides(prev => ({ ...prev, [fromHex]: toHex }));
      setEditingSwatch(null);
      // Propagate to the rest of the design AFTER the image's own recolor
      // succeeded — if the API call above failed, nothing should change on
      // canvas either (both sides of a locked-swatch edit stay consistent
      // with each other, never partially applied).
      if (toHex !== null && isLockedSwatch(fromHex) && canvas && activeSide) {
        replaceColorEverywhere(canvas, activeSide, fromHex, toHex);
      }
    } catch {
      showToast(t('bgColor.recolorFailed'), 'error');
    } finally {
      setBusy(null);
      setBusyHex(null);
    }
  };

  // "Colors currently in the design" for the total-count readout: the
  // quantized palette once conversion has actually run, otherwise the raw
  // detected-colors swatches (from detectColorsForStep, auto-loaded on
  // entering this step) — so the count/warning are meaningful even before
  // the user turns "Convert to N colors" on. Full Color never populates
  // `quantizedPalette` (selectFullColor is a no-op quantization — see its own
  // comment), so `convertEnabled` alone isn't enough to prefer it here; the
  // detected `colors` readout is the right answer in both the "not converted
  // yet" AND the "Full Color, nothing to convert" cases.
  // Foreground-only count — used for the "exceeds the N color limit" warning
  // below, which must NOT count the background swatch: the background color
  // is an intentional, separate addition on top of the chosen N-color
  // foreground limit (matching the reference tool's own "N colors + 1
  // background" convention), not something that should ever read as
  // "you've gone over your limit." activeColorCount (foreground + background)
  // is still the right number for the plain "Total color count" readout below.
  const foregroundColorCount = convertEnabled && selectedColorOption !== FULL_COLOR ? quantizedPalette.length : colors.length;
  const activeColorCount = foregroundColorCount + (backgroundColorEnabled ? 1 : 0);

  const cropTotalPercent = Math.min(cropInsets.top + cropInsets.bottom, 90) + Math.min(cropInsets.left + cropInsets.right, 90);
  const hasCropOrRotation = cropInsets.top > 0 || cropInsets.right > 0 || cropInsets.bottom > 0 || cropInsets.left > 0 || rotation !== 0;

  // Image's own embedded DPI (sharp's `density`, see uploads.ts) — undefined
  // whenever the source file carries no resolution tag at all (common for
  // web-exported PNGs), in which case no DPI line is shown rather than
  // reporting a misleading default.
  const dpiInfo = staged.originalDensity
    ? { dpi: Math.round(staged.originalDensity), quality: densityQuality(staged.originalDensity) }
    : null;

  // Step 4 (cropRotate) — Bakes the current insets + rotation into a brand-new image:
  // draws the (optionally rotated) source onto an offscreen canvas sized to
  // just the surviving cropped region, then reads that back out as a PNG
  // data URL. Same math as the ImageEditPreview's drag-crop rectangle, which
  // feeds `cropInsets` in the same top/right/bottom/left percent-of-rotated-
  // bounds shape this function already expects — the crop UI changed from
  // sliders to a drag rectangle, but the bake step itself needed no changes.
  const applyCropRotate = async () => {
    setBusy('crop');
    try {
      const el = await loadImageElement(previewUrl);
      const srcW = el.naturalWidth, srcH = el.naturalHeight;

      const radians = (rotation * Math.PI) / 180;
      const rotatedW = Math.abs(srcW * Math.cos(radians)) + Math.abs(srcH * Math.sin(radians));
      const rotatedH = Math.abs(srcW * Math.sin(radians)) + Math.abs(srcH * Math.cos(radians));

      const rotateCanvas = document.createElement('canvas');
      rotateCanvas.width = Math.round(rotatedW);
      rotateCanvas.height = Math.round(rotatedH);
      const rotateCtx = rotateCanvas.getContext('2d');
      if (!rotateCtx) throw new Error('no 2d context');
      rotateCtx.translate(rotateCanvas.width / 2, rotateCanvas.height / 2);
      rotateCtx.rotate(radians);
      rotateCtx.drawImage(el, -srcW / 2, -srcH / 2, srcW, srcH);

      const insetTop = Math.round((cropInsets.top / 100) * rotateCanvas.height);
      const insetBottom = Math.round((cropInsets.bottom / 100) * rotateCanvas.height);
      const insetLeft = Math.round((cropInsets.left / 100) * rotateCanvas.width);
      const insetRight = Math.round((cropInsets.right / 100) * rotateCanvas.width);
      const cropW = Math.max(1, rotateCanvas.width - insetLeft - insetRight);
      const cropH = Math.max(1, rotateCanvas.height - insetTop - insetBottom);

      const outCanvas = document.createElement('canvas');
      outCanvas.width = cropW;
      outCanvas.height = cropH;
      const outCtx = outCanvas.getContext('2d');
      if (!outCtx) throw new Error('no 2d context');
      outCtx.drawImage(rotateCanvas, insetLeft, insetTop, cropW, cropH, 0, 0, cropW, cropH);

      const dataUrl = outCanvas.toDataURL('image/png');
      setPreviewUrl(dataUrl);
      setHasEdits(true);
      goToNextStep();
    } catch {
      showToast(t('bgColor.cropFailed'), 'error');
    } finally {
      setBusy(null);
    }
  };

  const resetToOriginal = () => {
    setPreviewUrl(staged.originalUrl);
    setHasEdits(false);
    setColors([]);
    setColorsLoadedForStep(null);
    setBgOrColorMode('background');
    resetColorLimitState();
    committedPrintColorsRef.current = null;
    setSelectedColorOption(defaultColorOption);
    setCropInsets(DEFAULT_CROP);
    setRotation(0);
    setStepIndex(0);
    setWizardDone(false);
  };

  // Places the final edited (or untouched, if every step was "Keep Original")
  // image onto the design canvas exactly once, fitted to the design area —
  // this is the moment the image stops being "staged" and becomes a real
  // Fabric object, same fit/center behavior `addImageFitted` always had, just
  // deferred to the end of the wizard instead of running at upload time.
  const commitToCanvas = async () => {
    if (!canvas) { onDone(); return; }
    setBusy('placing');
    // See designStore.ts's own pendingCanvasCommits comment for the real bug
    // this closes — this commit runs an async fabric.Image.fromURL/setSrc
    // load before actually mutating the canvas; without this guard, a
    // side/tab switch could dispose the canvas out from under this commit's
    // still-pending callback, silently losing whatever it was about to add.
    useDesignStore.getState().beginCanvasCommit();
    try {
      const { fabric } = await import('fabric');

      // Update path: swap the EXISTING object's own image element via
      // Fabric's setSrc instead of adding a fresh object — this is what
      // preserves the user's current position/scale/rotation/flip/layer-
      // order, none of which addImageFitted (below) respects, since that
      // helper always re-centers and re-fits as if this were a brand-new
      // placement. originalUploadId/originalUploadUrl are normally left
      // untouched (still point at the pristine upload this correction ran
      // against), same as the fresh-placement path — EXCEPT when this wizard
      // was staged via a fresh re-import (ImageCorrectionModal.tsx's
      // importCatalogAssetAsUpload path, taken whenever the object had no
      // TRUSTED originalUploadId — absent, or inherited from a template via
      // its own `fromTemplate` flag). In that case `staged.uploadId` IS the
      // new trusted id, and must be written back onto the object — otherwise
      // `fromTemplate` stays true and originalUploadId keeps pointing at the
      // old foreign id forever, forcing every future "Correct Colors" open on
      // this same object to re-import from scratch again even after this fix
      // already resolved it once.
      if (existingObject) {
        // Captured BEFORE setSrc() so scaleToPreserveFootprint() can restore
        // this exact rendered size afterward — REGRESSION (found 2026-09-11,
        // 10th Design Guide PDF loop extension): a crop via this wizard's
        // Crop/Rotate step (applyCropRotate, above) bakes a genuinely
        // smaller-pixel PNG with no compensating scale, and Fabric's own
        // setSrc() overwrites width/height from the new image but never
        // touches scaleX/scaleY — see that helper's own comment for the full
        // investigation (Fabric source reference + reproduction) and why the
        // comment below about setSrc "preserving scale" isn't actually true
        // once pixel dimensions change.
        const priorFootprintW = existingObject.width * existingObject.scaleX;
        const priorFootprintH = existingObject.height * existingObject.scaleY;
        await new Promise<void>(resolve => {
          existingObject.setSrc(previewUrl, () => {
            existingObject.set({
              ...scaleToPreserveFootprint(existingObject, priorFootprintW, priorFootprintH),
              ...(committedPrintColorsRef.current?.length
                ? { printColors: committedPrintColorsRef.current }
                : {}),
              ...(existingObject.fromTemplate
                ? { originalUploadId: staged.uploadId, originalUploadUrl: staged.originalUrl, fromTemplate: false }
                : {}),
            });
            existingObject.setCoords();
            canvas.renderAll();
            // originalUploadId is deliberately UNCHANGED here in the common
            // (non-fromTemplate) case, but this object's actual rendered
            // content just changed (setSrc above) — invalidate the thin-line
            // hook's cache for it, or the "Continue with this logo"/badge
            // would keep showing whatever the check found before this edit,
            // not the just-committed result. See invalidateThinLineCheck's
            // own comment for why the backend itself needs no equivalent
            // fix (it already reads the latest processed variant every time).
            invalidateThinLineCheck(existingObject.originalUploadId ?? staged.uploadId);
            // setSrc()/set()/renderAll() above fire no Fabric canvas event on
            // their own (this is a programmatic swap-in-place, not a user
            // drag/resize) — useThinLineCheck.ts's scan only re-runs on
            // object:added/object:modified or its own background poll, so
            // without this the cache invalidation above would sit unused
            // until something UNRELATED happened to touch the canvas next.
            // A synthetic 'object:modified' is the standard Fabric idiom for
            // "this object genuinely changed programmatically" and is exactly
            // true here.
            canvas.fire('object:modified', { target: existingObject });
            resolve();
          }, { crossOrigin: 'anonymous' });
        });
        onDone();
        return;
      }

      await new Promise<void>(resolve => {
        fabric.Image.fromURL(previewUrl, (img: any) => {
          // originalUploadId/originalUploadUrl point back at the pristine
          // upload even though the object's own `src` is the fully-edited
          // result — this is what lets order fulfillment recover the
          // untouched original for a correction/reprint, and what lets a
          // later re-open of this same wizard from PropertyPanel.tsx's
          // "Correct Colors" button (see the existingObject path above)
          // restage from the true original rather than this already-edited
          // result.
          //
          // printColors: the resolved hex palette this image was actually
          // quantized to (Select Colors step) — per direct user request that
          // the printing-team Design Guide PDF show "what colors they have
          // picked for printing" per image; omitted entirely (not an empty
          // array) for full-color mode / a skipped step, matching the user's
          // own "if full color it is not required" instruction.
          //
          // Reads committedPrintColorsRef, NOT the live convertEnabled/
          // quantizedPalette state — by the time commitToCanvas runs
          // (wizardDone, i.e. the user has advanced past every step
          // including selectColors), goToNextStep's resetColorLimitState()
          // has already cleared quantizedPalette back to [] and convertEnabled
          // back to false regardless of what was picked, so that live state
          // can never reflect a real choice here. committedPrintColorsRef is
          // the one snapshot taken at the correct moment (see its own
          // comment) that survives to this point. Reported live: customer
          // picked "2" in Select Colors, wizard preview correctly showed a
          // 2-color quantized image, yet the Design Guide PDF still printed
          // "Full color — no color limit applied" for it.
          img.set({
            originalUploadId: staged.uploadId,
            originalUploadUrl: staged.originalUrl,
            ...(committedPrintColorsRef.current?.length
              ? { printColors: committedPrintColorsRef.current }
              : {}),
          });
          // Per direct user request: the thin-line check on this brand-new
          // placement should reflect what the user actually committed (e.g.
          // a Select Colors quantization down to 1 color that flattened away
          // the original artwork's fine detail), not the pristine upload —
          // the opposite tradeoff from resolveThinLineCheckSource's default
          // 'original' pin for a first check (imageProcessing.ts's own
          // 2026-08-19 comment). Marking staged.uploadId as a "re-edit" here
          // (same one-shot mechanism BackgroundColorTool's existingObject
          // branch above uses) makes the scan that addImageFitted's
          // object:added event triggers next request the LATEST processed
          // variant (the quantized result) instead of 'original'. Safe to
          // call even when nothing was actually quantized (Full Color /
          // "Skip this") — latest.png is byte-identical to original in that
          // case, so the result is unchanged either way.
          invalidateThinLineCheck(staged.uploadId);
          addImageFitted(canvas, img);
          resolve();
        }, { crossOrigin: 'anonymous' });
      });
      onDone();
    } finally {
      setBusy(null);
      useDesignStore.getState().endCanvasCommit();
    }
  };

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
      <ImageEditPreview
        imageUrl={previewUrl}
        rotation={step === 'cropRotate' ? rotation : 0}
        cropMode={step === 'cropRotate' && !wizardDone}
        cropInsets={cropInsets}
        onCropInsetsChange={setCropInsets}
        highlightHex={step === 'selectColors' ? hoveredSwatchHex : null}
      />

      {/* Upload's own size + embedded DPI — per direct user request, placed
          "below the preview section". SVG uploads show nothing here (no
          fixed pixel resolution/density to report — see
          StagedImage.originalWidth above); raster uploads show pixel size
          once known, and the DPI line only when the source FILE itself
          carried a resolution tag (many web-exported images don't — see
          originalDensity's own comment). This intentionally reports the
          image's OWN resolution, not an estimate against a print size —
          this codebase has no reliable physical print-size signal available
          at this point in the wizard today. */}
      {staged.originalWidth && staged.originalHeight && (
        <div style={{ fontSize: 11, color: 'var(--text-muted)', display: 'flex', flexDirection: 'column', gap: 2 }}>
          <div>{t('bgColor.imageSize', { width: staged.originalWidth, height: staged.originalHeight })}</div>
          {dpiInfo && (
            <div style={{ color: dpiInfo.quality === 'good' ? 'var(--text-muted)' : '#b45309' }}>
              {t('bgColor.embeddedDpi', { dpi: dpiInfo.dpi })}
              {' — '}
              {dpiInfo.quality === 'good' ? t('bgColor.dpiGood') : t('bgColor.dpiLow')}
            </div>
          )}
        </div>
      )}

      {/* Opens a larger zoomable side-by-side view (ImageCompareModal) — per
          direct user request for a way to "compare original and current" at
          real zoom levels, which the small 240px inline preview above
          doesn't have room for. Available on every step, not just after an
          edit — comparing against the original is useful before deciding
          whether to apply a step at all. */}
      <button
        onClick={() => setCompareOpen(true)}
        className="btn btn-secondary"
        style={{ width: '100%', fontSize: 12 }}
      >
        {t('bgColor.compareButton')}
      </button>
      {compareOpen && (
        <ImageCompareModal
          originalUrl={staged.originalUrl}
          currentUrl={previewUrl}
          onClose={() => setCompareOpen(false)}
        />
      )}

      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
        <div style={{ fontSize: 12, fontWeight: 600, color: 'var(--text-muted)' }}>{t('bgColor.title')}</div>
        {!wizardDone && (
          <div style={{ fontSize: 11, color: 'var(--text-muted)' }}>
            {t('bgColor.stepProgress', { current: stepIndex + 1, total: effectiveStepOrder.length })}
          </div>
        )}
      </div>

      {/* Progress dots — filled for the current and completed steps, hollow
          for steps not yet reached. Purely visual; clicking does nothing —
          this is a strict linear wizard, not a tab bar, per user request. */}
      {!wizardDone && (
        <div style={{ display: 'flex', gap: 6, justifyContent: 'center' }}>
          {effectiveStepOrder.map((s, i) => (
            <div key={s} style={{
              width: 8, height: 8, borderRadius: '50%',
              background: i <= stepIndex ? 'var(--selection)' : 'var(--border)',
            }} />
          ))}
        </div>
      )}

      {/* Completion state — the final "Place on canvas" action lives here:
          the staged image only ever touches the real design canvas once the
          user reaches this point (or explicitly resets/cancels). */}
      {wizardDone && (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 8, alignItems: 'center', padding: '8px 0' }}>
          <div style={{ fontSize: 13, fontWeight: 600 }}>{t('bgColor.allStepsDone')}</div>
          {/* Blue per direct user request — distinguishes the wizard's single
              committing action (places the staged image onto the real
              design canvas) from the neutral-gray per-step action buttons
              above it. */}
          <button
            onClick={commitToCanvas}
            disabled={busy !== null}
            style={{
              width: '100%', fontSize: 13, fontWeight: 600, padding: '10px 16px',
              borderRadius: 6, border: 0, cursor: 'pointer', background: 'var(--selection)', color: 'white',
            }}
          >
            <ButtonLabel
              busy={busy === 'placing'}
              label={
                busy === 'placing'
                  ? (existingObject ? t('bgColor.updating') : t('bgColor.placing'))
                  : (existingObject ? t('bgColor.updateImage') : t('bgColor.placeOnCanvas'))
              }
            />
          </button>
          <button onClick={startOver} disabled={busy !== null} style={keepOriginalBtn}>
            {t('bgColor.startOver')}
          </button>
        </div>
      )}

      {/* This step never renders for an SVG upload at all — it's excluded from
          effectiveStepOrder entirely (see that constant's own comment), not
          just disabled/skippable, since an upload that's already a vector
          has nothing for "Convert to Vector" to do. */}
      {!wizardDone && step === 'vectorize' && (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 4, fontSize: 13, fontWeight: 600 }}>
            {t('bgColor.stepVectorizeTitle', { number: stepNumberFor('vectorize') })}
            <InfoTooltip text={t('bgColor.stepVectorizeHint')} />
          </div>
          <button onClick={vectorize} disabled={busy !== null} className="btn btn-secondary" style={{ width: '100%', fontSize: 12 }}>
            <ButtonLabel busy={busy === 'vectorize'} label={busy === 'vectorize' ? t('bgColor.vectorizing') : t('bgColor.vectorize')} />
          </button>
          <button onClick={goToNextStep} disabled={busy !== null} style={keepOriginalBtn}>
            {t('bgColor.skipThisStep')}
          </button>
        </div>
      )}

      {!wizardDone && step === 'removeBackgroundOrColor' && (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 4, fontSize: 13, fontWeight: 600 }}>
            {t('bgColor.stepRemoveBackgroundOrColorTitle', { number: stepNumberFor('removeBackgroundOrColor') })}
            {bgOrColorMode === 'color' && <InfoTooltip text={t('bgColor.modeRemoveHint')} />}
          </div>

          {/* Mode toggle — defaults to 'background' per direct user request
              ("by default give background removal"); switching to 'color'
              reveals the same detected-swatches UI the old separate "Remove
              Selected Color" step had, with colors now loading automatically
              (see the useEffect above) rather than behind their own button.
              "Remove Background" is disabled for SVG uploads — its endpoint
              rejects vector input server-side (see isSvgUpload above), so
              leaving it clickable would just present a button that always
              fails with an error toast, same gap as the vectorize step. */}
          <div style={{ display: 'flex', gap: 4, border: '1px solid var(--border)', borderRadius: 6, padding: 2 }}>
            <button
              onClick={() => { setBgOrColorMode('background'); }}
              disabled={busy !== null || isSvgUpload}
              title={isSvgUpload ? t('bgColor.modeBackgroundNotApplicableSvg') : undefined}
              style={{
                flex: 1, fontSize: 12, padding: '6px 8px', borderRadius: 4, border: 0,
                cursor: isSvgUpload ? 'not-allowed' : 'pointer',
                background: bgOrColorMode === 'background' ? 'var(--selection)' : 'transparent',
                color: isSvgUpload ? 'var(--border)' : (bgOrColorMode === 'background' ? 'white' : 'var(--text-muted)'),
              }}
            >
              {t('bgColor.modeBackground')}
            </button>
            <button
              onClick={() => { setBgOrColorMode('color'); setColorsLoadedForStep(null); colorsFetchInFlightForStepRef.current = null; }}
              disabled={busy !== null}
              style={{
                flex: 1, fontSize: 12, padding: '6px 8px', borderRadius: 4, border: 0, cursor: 'pointer',
                background: bgOrColorMode === 'color' ? 'var(--selection)' : 'transparent',
                color: bgOrColorMode === 'color' ? 'white' : 'var(--text-muted)',
              }}
            >
              {t('bgColor.modeColor')}
            </button>
          </div>

          {bgOrColorMode === 'background' && (
            <button onClick={removeBackground} disabled={busy !== null} className="btn btn-secondary" style={{ width: '100%', fontSize: 12 }}>
              <ButtonLabel busy={busy === 'remove'} label={busy === 'remove' ? t('bgColor.removing') : t('bgColor.removeBackground')} />
            </button>
          )}

          {bgOrColorMode === 'color' && (
            <>
              {busy === 'detect' && colors.length === 0 && (
                <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 6, padding: '8px 0', fontSize: 12, color: 'var(--text-muted)' }}>
                  <Spinner size={14} />
                  {t('bgColor.detecting')}
                </div>
              )}
              {colorsLoadedForStep === 'removeBackgroundOrColor' && colors.length > 0 && (
                <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap' }}>
                  {colors.map(hex => (
                    <button
                      key={hex}
                      title={t('bgColor.removeThisColor', { hex })}
                      onClick={() => removeColor(hex)}
                      disabled={busy !== null}
                      style={{
                        width: 32, height: 32, borderRadius: 6, background: hex, border: '1px solid var(--border)', cursor: 'pointer',
                        display: 'flex', alignItems: 'center', justifyContent: 'center',
                      }}
                    >
                      {/* Swatches have no text label to swap to "Removing…" —
                          overlaying the spinner directly on the clicked swatch
                          gives the same immediate "this is working" feedback
                          the labeled buttons get from ButtonLabel. */}
                      {busy === 'removeColor' && busyHex === hex && <Spinner size={14} color="white" />}
                    </button>
                  ))}
                </div>
              )}
            </>
          )}

          <div style={stepFooter}>
            <button onClick={goToPreviousStep} disabled={busy !== null} style={backBtn}>{t('bgColor.back')}</button>
            <button onClick={goToNextStep} disabled={busy !== null} style={{ ...keepOriginalBtn, flex: 1 }}>
              {t('bgColor.skipThisStep')}
            </button>
          </div>
        </div>
      )}

      {/* This method has real physical-print-SIZE tiers instead of a
          color-count concept (PFConcept's DTF Transfer/Lasergraveren/etc —
          see hasAreaSizeTiers's own comment). A completely separate, much
          simpler render branch from the color-count UI below: no
          quantize-colors call ever happens here (these methods are always
          full-color), so there's no palette/swatch-editor/busy-spinner
          machinery to wire up — picking a tier just records the choice,
          same as clicking Full Color in the color-count picker does. */}
      {!wizardDone && step === 'selectColors' && hasAreaSizeTiers && (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 4, fontSize: 13, fontWeight: 600 }}>
            {t('bgColor.stepAreaSizeTitle', { number: stepNumberFor('selectColors') })}
            <InfoTooltip text={t('bgColor.areaSizeHint')} />
          </div>

          <div style={{ fontSize: 11, fontWeight: 600, color: 'var(--text-muted)' }}>{t('bgColor.areaSizeLabel')}</div>
          <div style={{ display: 'flex', gap: 4, flexWrap: 'wrap' }}>
            {printAreaSizeOptions.map(opt => (
              <button
                key={opt}
                onClick={() => {
                  setSelectedAreaSizeOption(opt);
                  setHasEdits(true);
                  setAreaSizeEdited(true);
                }}
                disabled={busy !== null}
                style={{
                  flex: 1, fontSize: 13, fontWeight: 600, padding: '6px 8px', borderRadius: 6, border: 0, cursor: 'pointer', whiteSpace: 'nowrap',
                  background: selectedAreaSizeOption === opt ? 'var(--selection)' : 'var(--surface-secondary, #f3f4f6)',
                  color: selectedAreaSizeOption === opt ? 'white' : 'var(--text-muted)',
                }}
              >
                {t('bgColor.areaSizeCm2', { size: opt })}
              </button>
            ))}
          </div>

          {/* Same "explicit commit action" convention as applyColorLimit
              below (color-count branch) — mirrors every other step's own
              commit button. Gated on areaSizeEdited (this step's own
              dedicated flag, set true only by the tier click above), NOT
              convertEnabled/hasEdits — see areaSizeEdited's own comment for
              why the shared hasEdits can already be true on step entry. */}
          {areaSizeEdited && (
            <button onClick={goToNextStep} disabled={busy !== null} className="btn btn-secondary" style={{ width: '100%', fontSize: 12 }}>
              {t('bgColor.applyAreaSize')}
            </button>
          )}

          <div style={stepFooter}>
            <button onClick={goToPreviousStep} disabled={busy !== null} style={backBtn}>{t('bgColor.back')}</button>
            <button onClick={goToNextStep} disabled={busy !== null} style={{ ...keepOriginalBtn, flex: 1 }}>
              {t('bgColor.skipThisStep')}
            </button>
          </div>
        </div>
      )}

      {!wizardDone && step === 'selectColors' && !hasAreaSizeTiers && (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 4, fontSize: 13, fontWeight: 600 }}>
            {t('bgColor.stepColorLimitTitle', { number: stepNumberFor('selectColors') })}
            <InfoTooltip text={t('bgColor.colorLimitHint')} />
          </div>

          <div style={{ fontSize: 11, fontWeight: 600, color: 'var(--text-muted)' }}>{t('bgColor.colorLimitLabel')}</div>
          {/* Clicking an option directly applies it — no separate "Convert"
              checkbox, per direct user request ("remove the checkbox instead
              of on selection of 1,2,3,4... apply to preview and show the
              color tiles"). Re-clicking a DIFFERENT number re-quantizes from
              wherever the previous quantize left off (the backend's
              quantize-colors defaults to its most-recently-written result,
              see readBestSourceRaster's latest.png convention), so 1→2→3→4
              chains rather than each jumping back to the pristine original —
              per the user's explicit "conversion need to happen from
              previous step result." Re-clicking the ALREADY-selected option
              is a no-op (skipped below) since nothing changed. Options come
              from the admin's ProductConfig.printColorOptions (narrowed by
              `?printColors=` — see useProductSetup.ts), not a fixed [1,2,3,4]. */}
          <div style={{ display: 'flex', gap: 4, flexWrap: 'wrap' }}>
            {printColorOptions.map(opt => (
              <button
                key={printColorOptionToString(opt)}
                onClick={() => {
                  setSelectedColorOption(opt);
                  if (opt !== selectedColorOption || !convertEnabled) {
                    if (opt === FULL_COLOR) selectFullColor();
                    else smartQuantizeColors(opt);
                  }
                }}
                disabled={busy !== null}
                style={{
                  flex: 1, fontSize: 13, fontWeight: 600, padding: '6px 8px', borderRadius: 6, border: 0, cursor: 'pointer', whiteSpace: 'nowrap',
                  background: selectedColorOption === opt ? 'var(--selection)' : 'var(--surface-secondary, #f3f4f6)',
                  color: selectedColorOption === opt ? 'white' : 'var(--text-muted)',
                }}
              >
                {translatedColorOptionLabel(productConfig, activeMethodForColorTranslation, opt, t('bgColor.fullColor'))}
              </button>
            ))}
          </div>

          {/* busyHex === null distinguishes a quantize-colors call (whole-
              palette, no specific swatch) from a recolor-swatch call (always
              sets busyHex) — shown on every quantize, not just the first,
              so re-clicking a different number while already converted
              still gives feedback instead of the preview appearing to just
              silently sit still until the new result lands. */}
          {busy === 'recolor' && busyHex === null && (
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 6, padding: '8px 0', fontSize: 12, color: 'var(--text-muted)' }}>
              <Spinner size={14} />
              {t('bgColor.quantizing')}
            </div>
          )}

          {/* Full Color has no quantized palette to edit (nothing was
              flattened — see selectFullColor's own comment), so the
              per-swatch recolor/transparent editor below only makes sense
              for a numeric limit; the detected-colors count above already
              covers Full Color's "what's in the design" readout. */}
          {convertEnabled && selectedColorOption !== FULL_COLOR && quantizedPalette.length > 0 && (
            <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap' }}>
              {/* Image-derived (unlocked) swatches first/left, locked
                  (already-used-elsewhere-in-the-design) swatches last/right
                  — per direct user request to visually separate "this step
                  picked it from the image" from "this was already spoken
                  for by another object," rather than interleaving them in
                  whatever order quantize-colors/recolor-swatch happened to
                  return. A stable partition (not a full re-sort) so swatches
                  don't reshuffle relative to their same-lock-state siblings
                  on every edit — only which SIDE of the divide they're on
                  can change. */}
              {[...quantizedPalette]
                .map((hex, i) => ({ hex, i, locked: isLockedSwatch(hex) }))
                .sort((a, b) => (Number(a.locked) - Number(b.locked)) || (a.i - b.i))
                .map(({ hex, locked }) => (
                  <SwatchWithEditor
                    key={hex}
                    hex={hex}
                    locked={locked}
                    editing={editingSwatch === hex}
                    busy={busy}
                    busyHex={busyHex}
                    logoColors={colors}
                    printableColors={printableColors}
                    designColors={designColorHexPalette}
                    // designColorBudget already excludes THIS object's own full
                    // printColors array (see its own declaration above), so
                    // isAtLimit here correctly means "no room for a color this
                    // object doesn't already contain" — re-picking a color the
                    // object already has (including this very swatch's current
                    // value) is unaffected, same as every other isAtLimit
                    // consumer in this app.
                    isAtLimit={designColorBudget.isAtLimit}
                    onOpen={() => setEditingSwatch(editingSwatch === hex ? null : hex)}
                    onPick={to => recolorSwatch(hex, to)}
                    onTransparent={() => recolorSwatch(hex, null)}
                    onClose={() => setEditingSwatch(null)}
                    onHoverChange={hovering => setHoveredSwatchHex(hovering ? hex : null)}
                    dimmed={hoveredSwatchHex !== null && hoveredSwatchHex !== hex}
                  />
                ))}
            </div>
          )}

          {/* Background color — a flat fill counted as ONE extra print color
              behind this image, separate from the N-color foreground
              quantization above (mirrors the reference tool's own separate
              "Background color" section). Only offered once a foreground
              conversion is active — an unconverted (Full Color / not-yet-
              quantized) image has no defined "background" to add a flat
              color behind in the first place. */}
          {convertEnabled && selectedColorOption !== FULL_COLOR && (
            <div style={{ display: 'flex', flexDirection: 'column', gap: 6, paddingTop: 4, borderTop: '1px solid var(--border)' }}>
              <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                <div style={{ fontSize: 11, fontWeight: 600, color: 'var(--text-muted)' }}>{t('bgColor.backgroundColorLabel')}</div>
                <button
                  role="switch"
                  aria-checked={backgroundColorEnabled}
                  onClick={() => {
                    setBackgroundColorEnabled(v => !v);
                    setEditingBackgroundColor(false);
                  }}
                  disabled={busy !== null}
                  style={{
                    width: 34, height: 18, borderRadius: 999, border: 0, cursor: 'pointer', position: 'relative',
                    background: backgroundColorEnabled ? 'var(--selection)' : 'var(--border)', flexShrink: 0,
                  }}
                >
                  <span style={{
                    position: 'absolute', top: 2, left: backgroundColorEnabled ? 18 : 2, width: 14, height: 14,
                    borderRadius: '50%', background: 'white', transition: 'left 0.15s',
                  }} />
                </button>
              </div>

              {backgroundColorEnabled && (
                <SwatchWithEditor
                  hex={backgroundColorHex}
                  locked={false}
                  editing={editingBackgroundColor}
                  busy={null}
                  busyHex={null}
                  logoColors={colors}
                  printableColors={printableColors}
                  designColors={designColorHexPalette}
                  isAtLimit={false}
                  onOpen={() => setEditingBackgroundColor(v => !v)}
                  onPick={to => { setBackgroundColorHex(to ?? backgroundColorHex); setEditingBackgroundColor(false); }}
                  onTransparent={() => setEditingBackgroundColor(false)}
                  onClose={() => setEditingBackgroundColor(false)}
                  onHoverChange={hovering => setHoveredSwatchHex(hovering ? backgroundColorHex : null)}
                  dimmed={hoveredSwatchHex !== null && hoveredSwatchHex !== backgroundColorHex}
                />
              )}
            </div>
          )}

          <div style={{ fontSize: 12, color: 'var(--text-muted)' }}>
            {t('bgColor.totalColorCount', { count: activeColorCount })}
          </div>
          {selectedColorOption !== FULL_COLOR && foregroundColorCount > selectedColorOption && (
            <div style={{ fontSize: 11, color: '#ef4444' }}>{t('bgColor.colorLimitExceeded', { limit: selectedColorOption })}</div>
          )}

          {/* Explicit commit action — every other step in this wizard has one
              (Convert to Vector, Remove Background, Apply Crop/Rotate); this
              step was missing its equivalent, leaving "Skip this" as the only
              way to move on even AFTER the user had already converted/edited
              swatches, which reads as "discard what I just did" rather than
              "continue with it." Only shown once a conversion has actually
              been applied — before that, "Skip this" alone (no colors
              changed yet) is the correct and only path forward. */}
          {convertEnabled && (
            <button onClick={goToNextStep} disabled={busy !== null} className="btn btn-secondary" style={{ width: '100%', fontSize: 12 }}>
              {t('bgColor.applyColorLimit')}
            </button>
          )}

          <div style={stepFooter}>
            <button onClick={goToPreviousStep} disabled={busy !== null} style={backBtn}>{t('bgColor.back')}</button>
            <button onClick={goToNextStep} disabled={busy !== null} style={{ ...keepOriginalBtn, flex: 1 }}>
              {t('bgColor.skipThisStep')}
            </button>
          </div>
        </div>
      )}

      {!wizardDone && step === 'cropRotate' && (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 4, fontSize: 13, fontWeight: 600 }}>
            {t('bgColor.stepCropRotateTitle', { number: stepNumberFor('cropRotate') })}
            <InfoTooltip text={t('bgColor.cropDragHint')} />
          </div>

          <div style={{ fontSize: 11, fontWeight: 600, color: 'var(--text-muted)' }}>{t('bgColor.rotation')}</div>
          <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
            <input type="range" min={-180} max={180} step={1} value={rotation}
              onChange={e => setRotation(Number(e.target.value))} style={{ flex: 1 }} />
            <span style={{ fontSize: 11, color: 'var(--text-muted)', width: 34, textAlign: 'right' }}>{rotation}°</span>
          </div>
          <div style={{ display: 'flex', gap: 4 }}>
            {[-90, -5, 5, 90].map(d => (
              <button key={d} onClick={() => setRotation(r => ((r + d + 180) % 360 + 360) % 360 - 180)}
                disabled={busy !== null} style={nudgeBtn}>
                {d > 0 ? `+${d}°` : `${d}°`}
              </button>
            ))}
          </div>

          {cropTotalPercent >= 90 && (
            <div style={{ fontSize: 11, color: '#ef4444' }}>{t('bgColor.cropTooMuch')}</div>
          )}

          {/* Explicit blue/gray styling instead of the plain `btn-secondary`
              class — that class has no `:disabled` style at all (see
              index.css), so a disabled button was visually IDENTICAL to an
              enabled one, just non-clickable; reported live as "Apply Crop
              and Rotate should be disabled until user crop/rotate... once
              done change that button to blue color." Blue (`#2563eb`) once
              a crop/rotation is actually set matches this same file's
              existing convention for "the one real committing action" (the
              wizard's own "Place on Design" button uses the identical
              color), gray+not-allowed while there's nothing to apply yet. */}
          <button
            onClick={applyCropRotate}
            disabled={busy !== null || !hasCropOrRotation || cropTotalPercent >= 90}
            style={{
              width: '100%', fontSize: 12, fontWeight: 600, padding: '8px 16px', borderRadius: 6, border: 0,
              cursor: (busy !== null || !hasCropOrRotation || cropTotalPercent >= 90) ? 'not-allowed' : 'pointer',
              background: hasCropOrRotation && cropTotalPercent < 90 ? 'var(--selection)' : 'var(--border)',
              color: hasCropOrRotation && cropTotalPercent < 90 ? 'white' : 'var(--text-muted)',
            }}
          >
            <ButtonLabel busy={busy === 'crop'} label={busy === 'crop' ? t('bgColor.applying') : t('bgColor.applyCropRotate')} />
          </button>
          <div style={stepFooter}>
            <button onClick={goToPreviousStep} disabled={busy !== null} style={backBtn}>{t('bgColor.back')}</button>
            <button onClick={goToNextStep} disabled={busy !== null} style={{ ...keepOriginalBtn, flex: 1 }}>
              {t('bgColor.skipThisStep')}
            </button>
          </div>
        </div>
      )}

      {/* Visible, styled button (not a small text link) — shown whenever ANY
          step's action has run, per earlier direct user request that this
          control be reachable after any edit, not just background removal. */}
      {hasEdits && !wizardDone && (
        <button
          onClick={resetToOriginal}
          disabled={busy !== null}
          style={{
            width: '100%', fontSize: 13, fontWeight: 600, padding: '8px 10px',
            borderRadius: 6, border: '1px solid #ef4444', background: '#fef2f2',
            color: '#ef4444', cursor: 'pointer',
          }}
        >
          {t('bgColor.resetToOriginal')}
        </button>
      )}
    </div>
  );
}

function loadImageElement(url: string): Promise<HTMLImageElement> {
  return new Promise((resolve, reject) => {
    const img = new Image();
    img.crossOrigin = 'anonymous';
    img.onload = () => resolve(img);
    img.onerror = reject;
    img.src = url;
  });
}

const SWATCH_POPOVER_WIDTH = 220;
const SWATCH_POPOVER_HEIGHT = 460;
// At-limit mode drops the hex wheel/CMYK/printable-colors sections — just
// "select from logo" + the design-colors row + the PMS readout + Transparent
// — so it needs less room than the full picker, but slightly more than
// before the PMS readout became unconditional (see PmsReadout's own render
// site — it's the one row shown in BOTH modes now).
const SWATCH_POPOVER_AT_LIMIT_HEIGHT = 310;

interface SwatchWithEditorProps {
  hex: string;
  // True when this exact color is already used elsewhere in the design
  // (reserved into this palette by smartQuantizeColors' reserve-then-fill —
  // see isLockedSwatch) — shown with a small lock badge and, on edit,
  // requires confirmation since changing it ripples beyond this one staged
  // image (see recolorSwatch's own comment).
  locked: boolean;
  editing: boolean;
  busy: 'recolor' | null | string;
  busyHex: string | null;
  logoColors: string[];
  printableColors: PrintableColor[];
  // The design's own already-used colors (designColorHexPalette) + whether
  // the print-color budget is exhausted (designColorBudget.isAtLimit) — once
  // true, per direct user request ("once user reach the max color it give
  // only design colors as the option"), this swatch's editor hides its free
  // hex wheel/CMYK/printable-colors sections and offers ONLY these design
  // colors (plus "select from logo" and "Transparent", neither of which can
  // introduce a genuinely NEW color beyond what's already in the design/image).
  designColors: string[];
  isAtLimit: boolean;
  onOpen: () => void;
  onPick: (to: string | null) => void;
  onTransparent: () => void;
  onClose: () => void;
  /** Fires on pointer enter/leave — lifts this swatch's hex up to drive the live preview's hover-highlight (see hoveredSwatchHex). */
  onHoverChange?: (hovering: boolean) => void;
  /** True while a DIFFERENT swatch in this group is hovered — dims this one
   *  to 0.4 opacity so the hovered swatch (which stays at full opacity)
   *  reads as the clear focal point. Replaces the native browser tooltip
   *  this button used to show on hover (per direct user request — the
   *  unstyleable dark tooltip box "not looking good"); the locked-swatch
   *  explanation that tooltip used to carry now lives in a hover popover
   *  on the lock badge instead (see LockedSwatchBadge below). */
  dimmed?: boolean;
}

// Wraps a single quantized-palette swatch button together with its editor
// popover trigger ref — the ref is what lets the popover measure the
// swatch's actual on-screen position (getBoundingClientRect) instead of
// assuming it always has room to grow rightward from `left: 0`, which broke
// for any swatch near the right edge of the wrapped grid (reported live:
// the popover rendered mostly behind the canvas workspace panel that
// follows this tool sidebar in the layout, with no way to reach its Apply
// button).
function SwatchWithEditor({ hex, locked, editing, busy, busyHex, logoColors, printableColors, designColors, isAtLimit, onOpen, onPick, onTransparent, onClose, onHoverChange, dimmed = false }: SwatchWithEditorProps) {
  const { t } = useTranslation();
  const { triggerRef, pos } = usePopoverPosition(editing, SWATCH_POPOVER_WIDTH, isAtLimit ? SWATCH_POPOVER_AT_LIMIT_HEIGHT : SWATCH_POPOVER_HEIGHT);
  return (
    <div style={{ position: 'relative', opacity: dimmed ? 0.4 : 1, transition: 'opacity 0.15s' }}>
      <button
        ref={triggerRef as React.RefObject<HTMLButtonElement>}
        onClick={onOpen}
        onMouseEnter={() => onHoverChange?.(true)}
        onMouseLeave={() => onHoverChange?.(false)}
        disabled={busy !== null}
        style={{
          width: 32, height: 32, borderRadius: 6, background: hex,
          border: editing ? '2px solid var(--selection)' : locked ? '1px solid var(--text-muted)' : '1px solid var(--border)',
          cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center',
        }}
      >
        {busy === 'recolor' && busyHex === hex && <Spinner size={14} color="white" />}
      </button>
      {locked && !(busy === 'recolor' && busyHex === hex) && (
        <span style={{ position: 'absolute', bottom: -6, right: -6 }}>
          <LockedSwatchBadge text={`${hex} — ${t('bgColor.lockedSwatchTitle')}`} />
        </span>
      )}
      {editing && pos && (
        <SwatchEditorPopover
          hex={hex}
          top={pos.top}
          left={pos.left}
          logoColors={logoColors}
          printableColors={printableColors}
          designColors={designColors}
          isAtLimit={isAtLimit}
          busy={busy !== null}
          onPick={onPick}
          onTransparent={onTransparent}
          onClose={onClose}
        />
      )}
    </div>
  );
}

const LOCKED_BADGE_POPOVER_WIDTH = 200;

// Small lock-icon badge on a locked swatch, with the same "already used
// elsewhere" explanation the badge used to carry as a native `title`
// tooltip — moved to a real hover popover (same portaled/viewport-clamped
// pattern as InfoTooltip.tsx and every other popover in this file) per
// direct user request that the unstyleable native tooltip box "not looking
// good" be replaced, not just removed. Kept as its own small component
// (rather than reusing InfoTooltip.tsx directly) so the trigger can keep
// showing the actual Lock icon instead of InfoTooltip's hardcoded (i) —
// the lock is the primary "this one's special" signal at a glance; hovering
// it explains why.
function LockedSwatchBadge({ text }: { text: string }) {
  const [open, setOpen] = useState(false);
  const closeTimer = useRef<ReturnType<typeof setTimeout> | null>(null);
  const { triggerRef, pos } = usePopoverPosition(open, LOCKED_BADGE_POPOVER_WIDTH, 70);

  const show = () => {
    if (closeTimer.current) { clearTimeout(closeTimer.current); closeTimer.current = null; }
    setOpen(true);
  };
  const scheduleHide = () => { closeTimer.current = setTimeout(() => setOpen(false), 120); };

  return (
    <span style={{ display: 'inline-flex', position: 'relative' }} onMouseEnter={show} onMouseLeave={scheduleHide}>
      <button
        ref={triggerRef as React.RefObject<HTMLButtonElement>}
        type="button"
        onFocus={show}
        onBlur={scheduleHide}
        onClick={() => setOpen(o => !o)}
        aria-label={text}
        style={{
          width: 16, height: 16, borderRadius: '50%', padding: 0, cursor: 'pointer',
          background: 'white', border: '1px solid var(--border)', display: 'flex', alignItems: 'center', justifyContent: 'center',
        }}
      >
        <Lock size={9} strokeWidth={2.5} color="var(--text-muted)" />
      </button>
      {open && pos && (
        <PortaledPopover top={pos.top} left={pos.left} width={LOCKED_BADGE_POPOVER_WIDTH} onClose={() => setOpen(false)}>
          <div onMouseEnter={show} onMouseLeave={scheduleHide} style={{ fontSize: 12, lineHeight: 1.4, color: 'var(--text)' }}>
            {text}
          </div>
        </PortaledPopover>
      )}
    </span>
  );
}

interface SwatchEditorPopoverProps {
  hex: string;
  top: number;
  left: number;
  /** The full (pre-quantize) detected-colors list — "select from logo" reuses these, matching the reference UI's "Select from logo" row. */
  logoColors: string[];
  printableColors: PrintableColor[];
  /** The design's own already-used colors — see SwatchWithEditorProps.designColors for the full rationale. */
  designColors: string[];
  isAtLimit: boolean;
  busy: boolean;
  onPick: (hex: string) => void;
  onTransparent: () => void;
  onClose: () => void;
}

// Per-swatch editor opened by clicking a quantized-palette swatch — hex
// picker (react-colorful, same library ColorPicker.tsx already uses), a
// derived CMYK readout/editor, a "select from logo" row reusing the design's
// own already-detected colors, and a "Transparent" toggle. Mirrors the
// reference screenshots' "Pick your color" popover.
//
// Portaled onto document.body with viewport-clamped fixed coordinates
// (usePopoverPosition/PortaledPopover, shared with ColorPicker.tsx and
// VariantSelector.tsx's ColorPickerPopover — the original fix for this same
// clipping bug class) rather than `position:absolute` inside the swatch's
// own DOM subtree, so it can never be clipped by the tool sidebar's
// `overflow:hidden` or rendered underneath the canvas workspace panel.
//
// `isAtLimit` (true once the print-color budget has no room left for a
// genuinely NEW color) hides the hex wheel/CMYK/printable-colors sections
// entirely — per direct user request ("once user reach the max color it
// give only design colors as the option") — leaving only "select from logo"
// (the IMAGE's own colors, never introduces a color beyond what's already in
// the design), the design-colors row, and Transparent, none of which can
// push the design over its configured count.
function SwatchEditorPopover({ hex, top, left, logoColors, printableColors, designColors, isAtLimit, busy, onPick, onTransparent, onClose }: SwatchEditorPopoverProps) {
  const { t } = useTranslation();
  const [draftHex, setDraftHex] = useState(hex);
  const rgb = hexToRgb(draftHex.length === 7 ? draftHex : hex);
  const cmyk = rgbToCmyk(rgb.r, rgb.g, rgb.b);
  const swatchOnly = isAtLimit && designColors.length > 0;
  // True the moment the draft differs from the swatch's ORIGINAL color —
  // covers every way of picking a new one (hex wheel, CMYK inputs, "select
  // from logo," "Colors Used in Design," or typing a hex directly), since
  // they all funnel through the same setDraftHex. Drives the Apply button's
  // blue "--selection" styling below, per direct user request ("if the user
  // select color from below input... it should change the 'Apply' button
  // color blue") — previously Apply stayed the same gray regardless of
  // whether a pending pick existed, giving no visual confirmation a
  // selection had actually registered before clicking Apply.
  const hasPendingChange = normalizeColor(draftHex) !== normalizeColor(hex);

  const applyCmyk = (next: { c: number; m: number; y: number; k: number }) => {
    setDraftHex(cmykToHex(next.c, next.m, next.y, next.k));
  };

  return (
    <PortaledPopover top={top} left={left} width={SWATCH_POPOVER_WIDTH} onClose={onClose}>
      <div style={{ fontSize: 12, fontWeight: 600 }}>{t('bgColor.pickYourColor')}</div>

      {!swatchOnly && (
        <>
          <HexColorPicker color={draftHex} onChange={setDraftHex} style={{ width: '100%' }} />

          <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
            <div style={{ width: 20, height: 20, borderRadius: 4, background: draftHex, border: '1px solid var(--border)', flexShrink: 0 }} />
            <input
              value={draftHex}
              onChange={e => setDraftHex(e.target.value)}
              style={{ flex: 1, padding: '4px 6px', border: '1px solid var(--border)', borderRadius: 4, fontSize: 11, fontFamily: 'monospace' }}
            />
          </div>
        </>
      )}

      {logoColors.length > 0 && (
        <div>
          <div style={{ fontSize: 11, color: 'var(--text-muted)', marginBottom: 4 }}>{t('bgColor.selectFromLogo')}</div>
          <div style={{ display: 'flex', flexWrap: 'wrap', gap: 4 }}>
            {logoColors.map(c => (
              <button key={c} onClick={() => setDraftHex(c)} title={c}
                style={{ width: 18, height: 18, background: c, border: '1px solid rgba(0,0,0,0.1)', borderRadius: 3, cursor: 'pointer' }} />
            ))}
          </div>
        </div>
      )}

      {swatchOnly && designColors.length > 0 && (
        <div>
          <div style={{ fontSize: 11, color: 'var(--text-muted)', marginBottom: 4 }}>{t('colorsUsed.title')}</div>
          <div style={{ display: 'flex', flexWrap: 'wrap', gap: 4 }}>
            {designColors.map(c => (
              <button key={c} onClick={() => setDraftHex(c)} title={c}
                style={{ width: 18, height: 18, background: c, border: draftHex === c ? '2px solid var(--selection)' : '1px solid rgba(0,0,0,0.1)', borderRadius: 3, cursor: 'pointer' }} />
            ))}
          </div>
        </div>
      )}

      {!swatchOnly && (
        <>
          <div>
            <div style={{ fontSize: 11, color: 'var(--text-muted)', marginBottom: 4 }}>{t('bgColor.cmyk')}</div>
            <div style={{ display: 'flex', gap: 4 }}>
              {(['c', 'm', 'y', 'k'] as const).map(ch => (
                <input
                  key={ch}
                  type="number"
                  min={0}
                  max={100}
                  value={cmyk[ch]}
                  onChange={e => applyCmyk({ ...cmyk, [ch]: Math.max(0, Math.min(100, Number(e.target.value) || 0)) })}
                  style={{ width: 0, flex: 1, padding: '4px 2px', border: '1px solid var(--border)', borderRadius: 4, fontSize: 11, textAlign: 'center' }}
                />
              ))}
            </div>
          </div>

        </>
      )}

      {/* Outside both swatchOnly branches, deliberately — the PMS readout is
          informational (nearestPantone reads whatever the swatch's CURRENT
          hex already is, never introduces a new color), so it must keep
          showing even once the design's print-color budget is exhausted and
          the free hex wheel/CMYK/printable-colors picker above is hidden.
          Was previously nested inside the `!swatchOnly` block above — bug:
          once a design already used up its color budget elsewhere (isAtLimit
          true), the PMS pill silently vanished along with the picker
          controls, even though showing "what Pantone code is this swatch
          closest to" doesn't let the customer pick a new color at all.
          Reported live: user confirmed seeing the swatch editor popover but
          no PMS pill/icon on a real product URL — reproduced by forcing
          swatchOnly true, root-caused to this nesting. */}
      <PmsReadout match={nearestPantone(draftHex.length === 7 ? draftHex : hex, printableColors)} />

      <button onClick={onTransparent} disabled={busy} className="btn btn-secondary" style={{ fontSize: 11, padding: '4px 8px' }}>
        {t('bgColor.transparent')}
      </button>

      <div style={{ display: 'flex', gap: 6 }}>
        <button onClick={onClose} disabled={busy} style={{ ...backBtn, flex: 1 }}>{t('bgColor.back')}</button>
        <button
          onClick={() => onPick(draftHex)}
          disabled={busy}
          style={{
            flex: 1, fontSize: 12, fontWeight: 600, padding: '6px 10px', borderRadius: 6, border: 0,
            cursor: busy ? 'not-allowed' : 'pointer',
            // Blue ("--selection", this app's own "one real committing
            // action" convention — matches applyCropRotate's identical
            // pending/not-pending styling above) once ANY pick differs from
            // this swatch's original color, gray otherwise — see
            // hasPendingChange's own comment.
            background: hasPendingChange ? 'var(--selection)' : 'var(--surface-secondary, #f3f4f6)',
            color: hasPendingChange ? 'white' : 'var(--text-muted)',
          }}
        >
          <ButtonLabel busy={busy} label={busy ? t('bgColor.applying') : t('bgColor.apply')} />
        </button>
      </div>
    </PortaledPopover>
  );
}
const stepFooter: React.CSSProperties = {
  display: 'flex',
  gap: 8,
  position: 'sticky',
  bottom: 0,
  zIndex: 2,
  padding: '8px 0',
  background: 'var(--surface, #fff)',
  borderTop: '1px solid var(--border)',
};

const keepOriginalBtn: React.CSSProperties = {
  width: "100%",
  fontSize: 12,
  padding: "8px 12px",
  fontWeight: 600,
  borderRadius: 6,
 border: '1px solid #93c5fd',
  backgroundColor: '#dbeafe',
  color: '#1e40af',
  cursor: 'pointer',
  transition: 'background-color 0.2s ease',
};

const backBtn: React.CSSProperties = {
  fontSize: 12,
  padding: "8px 12px",
   fontWeight: 600,
  borderRadius: 6,
  border: '1px solid rgb(180 198 217)',
  backgroundColor: '#e2e8f0',
  color: "var(--text-muted)",
  cursor: "pointer",
};

const nudgeBtn: React.CSSProperties = {
  flex: 1, fontSize: 11, padding: '4px 6px', borderRadius: 4,
  border: '1px solid var(--border)', background: 'transparent',
  color: 'var(--text-muted)', cursor: 'pointer',
};
